import React, { useEffect, useState, useCallback, useMemo, useRef } from 'react';
import { useNavigate, useLocation, useBlocker, type BlockerFunction } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Typography,
  Alert,
  CircularProgress,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Paper,
  Stepper,
  Step,
  StepLabel,
  IconButton,
  Checkbox,
  FormControlLabel,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Chip,
  Tooltip,
  Autocomplete,
  Card,
  CardContent,
  useTheme,
  useMediaQuery,
  type SelectChangeEvent,
} from '@mui/material';
import {
  Add as AddIcon,
  AddCircleOutline as AddCircleIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  CheckCircle as CheckCircleIcon,
  Save as SaveIcon,
  ArrowBack as ArrowBackIcon,
  Close as CloseIcon,
  Lock as LockIcon,
  OpenInNew as OpenInNewIcon,
  Refresh as RefreshIcon,
} from '@mui/icons-material';
import {
  poursAPI,
  type PourCreate,
  type PourLookups,
} from '../services/pours';
import productsService, {
  type DefaultTarget,
  type Product,
  type ProductListItem,
  type ProductCalibrationLink,
  type TargetGroup,
  type SupplierPlant,
} from '../services/products';
import projectsService from '../services/projects';
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker';
import dayjs, { type Dayjs } from 'dayjs';
import { useAuth } from '../context/AuthContext';
import { hasPermission } from '../utils/userPermissions';
import { authAPI } from '../services';
import { isSuperAdmin } from '../utils/userPermissions';
import AzureAddressAutocomplete from '../components/AzureAddressAutocomplete';
import ImagePreviewDialog from '../components/pour/ImagePreviewDialog';
import ChannelRowsPicker from '../components/pour/ChannelRowsPicker';
import HardwareTypeCards, { HardwareTypeImage } from '../components/pour/HardwareTypeCards';
import LocationFunctionToggle from '../components/pour/LocationFunctionToggle';
import {
  CONCRETE_START_SHORT_LABEL,
  DialogStep,
  HARDWARE_TYPE_NAMES,
  NOT_APPLICABLE,
  TABLE_ICON_CELL_SX,
  TABLE_TICK_HEAD_SX,
  TABLE_NAME_CELL_SX,
  TABLE_TIME_CELL_SX,
} from '../components/pour/locationTableParts';
import DifferentialsCell, { type DiffPartnerOption } from '../components/pour/DifferentialsCell';
import HardwareNumberPicker, { type HardwareNumberOption } from '../components/pour/HardwareNumberPicker';
import { useChannelBatch } from '../components/pour/useChannelBatch';
import CalibrationCheckPanel from '../components/pour/CalibrationCheckPanel';
import { useCalibrationCheck } from '../components/pour/useCalibrationCheck';
import {
  DEFAULT_MATURITY_METHOD,
  acknowledgeFlag,
  calibrationCheckBlock,
  describeMethod,
  noCalibrationText,
  pourComputesMaturity,
} from '../utils/calibrationCheck';
import {
  CHANNEL_NUMBERS,
  LOCATION_NAME_MAX,
  isDuplicateLocationName,
  isLoggerFullyUsed,
  isMultiChannelHardware,
  locationNameKey,
  normalizeLocationName,
  type PourChannelRef,
} from '../utils/channelRows';
import { apiErrorText } from '../utils/apiError';
import { removeRowAndRenumber, setPartners, symmetricPartners } from '../utils/draftDiffs';
import { TIMEZONES, type TimezoneOption } from '../data/timezones';
import {
  formatDateTimeLabelled,
  localInputToUtc,
  timeZoneLabel,
  utcToLocalInput,
} from '../utils/datetime';
import {
  STOP_AFTER_DAYS,
  describeStop,
  parseXHours,
  stopSettingsErrors,
} from '../utils/stopMonitoring';
import StopMonitoringFields from '../components/pour/StopMonitoringFields';
import { collapseProductRevisions } from '../utils/products';
import {
  describeMaturity,
  formatMaturity,
  labelWithUnit,
  maturityShortTitle,
  targetRangeFlag,
  targetStrengthText,
  valueWithUnit,
} from '../utils/maturityDisplay';
import { locationFunctionView } from '../utils/locationFunction';
import { useCompanyStrengthUnits } from '../utils/useCompanyStrengthUnit';
import {
  deriveFunctionalityCode,
  monitoringFunctionsToString,
  FUNCTION_OPTIONS,
  type MonitoringFunctionId,
} from '../components/pour/PourFunctionSelector';
import {
  BRAND_GREEN,
  PRIMARY_ACTION_SX,
  SELECT_MENU_PROPS,
  DROPDOWN_AUTOCOMPLETE_SLOT_PROPS,
  PICKER_POPPER_SX,
  STEPPER_SX,
  mobileProgressSegmentSx,
} from '../components/pour/pourWizardStyles';

// The last step reviews the whole setup; SAVE creates the pour from there.
const STEPS = ['Function', 'Pour Details', 'Hardware / Sensors', 'Notifications / Alerts', 'Review'];
const REVIEW_STEP = STEPS.length - 1;

// Label shown for a mix in the Product/Recipe dropdown and the review step.
const formatProductOption = (p: ProductListItem): string => {
  const parts = [p.code];
  if (p.supplier_plant_name) parts.push(p.supplier_plant_name);
  if (p.description) parts.push(p.description);
  return parts.join(' · ');
};

/** The default Pour Start: the next half-hour boundary at least 10 minutes
 *  ahead (10:00 to 10:30, 10:25 to 11:00), matching the pickers' 30-minute
 *  steps and never in the past (which trips the invalid/red state). It is
 *  the wall clock in `timeZone`, or in the browser's own zone without one. */
const defaultPourStart = (timeZone?: string): string => {
  const ahead = new Date(Date.now() + 10 * 60000);
  const wallClock = timeZone
    ? utcToLocalInput(ahead, timeZone)
    : dayjs(ahead).format('YYYY-MM-DDTHH:mm');
  const m = wallClock.match(/^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})$/);
  if (!m) return wallClock;
  const [, y, mo, d, h, mi] = m;
  // Rounded as a calendar time (via UTC, so no daylight saving shift),
  // carrying into the next hour or day.
  return new Date(Date.UTC(+y, +mo - 1, +d, +h, Math.ceil(+mi / 30) * 30))
    .toISOString()
    .slice(0, 16);
};

const FUNCTION_DEPENDENCIES: Record<string, string[]> = {
  'Temperature Only': [],
  'Concrete Maturity': ['Temperature Only'],
  'Temperature Match Curing': ['Temperature Only', 'Concrete Maturity'],
  'External Temperature and Humidity': [],
  'Onsite Weather Conditions': [],
  'Calculate Concrete Evaporation Rate': ['Temperature Only'],
};

// ─── Field visibility per monitoring function ───
// TRUE = field is shown when this function is selected.
// A field is visible if ANY selected function has it as TRUE.
const FIELD_VISIBILITY: Record<string, Record<MonitoringFunctionId, boolean>> = {
  supplierPlant: {
    'Temperature Only': true,
    'Concrete Maturity': true,
    'Temperature Match Curing': true,
    'External Temperature and Humidity': false,
    'Onsite Weather Conditions': false,
    'Calculate Concrete Evaporation Rate': true,
  },
  productCode: {
    'Temperature Only': true,
    'Concrete Maturity': true,
    'Temperature Match Curing': true,
    'External Temperature and Humidity': false,
    'Onsite Weather Conditions': false,
    'Calculate Concrete Evaporation Rate': true,
  },
  maturityTargetGroup: {
    'Temperature Only': false,
    'Concrete Maturity': true,
    'Temperature Match Curing': true,
    'External Temperature and Humidity': false,
    'Onsite Weather Conditions': false,
    'Calculate Concrete Evaporation Rate': false,
  },
  massConcreteLimitGroup: {
    'Temperature Only': true,
    'Concrete Maturity': false,
    'Temperature Match Curing': false,
    'External Temperature and Humidity': false,
    'Onsite Weather Conditions': false,
    'Calculate Concrete Evaporation Rate': false,
  },
  evaporationLimitGroup: {
    'Temperature Only': false,
    'Concrete Maturity': false,
    'Temperature Match Curing': false,
    'External Temperature and Humidity': false,
    'Onsite Weather Conditions': false,
    'Calculate Concrete Evaporation Rate': true,
  },
  // ─── Hardware / Location fields ───
  channelNumber: {
    'Temperature Only': true,
    'Concrete Maturity': true,
    'Temperature Match Curing': true,
    'External Temperature and Humidity': false,
    'Onsite Weather Conditions': false,
    'Calculate Concrete Evaporation Rate': true,
  },
  locationFunction: {
    'Temperature Only': false,
    'Concrete Maturity': true,
    'Temperature Match Curing': false,
    'External Temperature and Humidity': true,
    'Onsite Weather Conditions': false,
    'Calculate Concrete Evaporation Rate': false,
  },
  tank: {
    'Temperature Only': false,
    'Concrete Maturity': false,
    'Temperature Match Curing': true,
    'External Temperature and Humidity': false,
    'Onsite Weather Conditions': false,
    'Calculate Concrete Evaporation Rate': false,
  },
  concreteTemperatureStartTime: {
    'Temperature Only': true,
    'Concrete Maturity': true,
    'Temperature Match Curing': true,
    'External Temperature and Humidity': false,
    'Onsite Weather Conditions': false,
    'Calculate Concrete Evaporation Rate': true,
  },
  evaporationStopTime: {
    'Temperature Only': false,
    'Concrete Maturity': false,
    'Temperature Match Curing': false,
    'External Temperature and Humidity': false,
    'Onsite Weather Conditions': false,
    'Calculate Concrete Evaporation Rate': true,
  },
  maturityStrength: {
    'Temperature Only': false,
    'Concrete Maturity': true,
    'Temperature Match Curing': false,
    'External Temperature and Humidity': false,
    'Onsite Weather Conditions': false,
    'Calculate Concrete Evaporation Rate': false,
  },
  temperatureLimits: {
    'Temperature Only': true,
    'Concrete Maturity': false,
    'Temperature Match Curing': false,
    'External Temperature and Humidity': true,
    'Onsite Weather Conditions': false,
    'Calculate Concrete Evaporation Rate': false,
  },
  temperatureDifferentials: {
    'Temperature Only': true,
    'Concrete Maturity': false,
    'Temperature Match Curing': false,
    'External Temperature and Humidity': true,
    'Onsite Weather Conditions': false,
    'Calculate Concrete Evaporation Rate': false,
  },
};

// Mass-concrete controls (limit group, Peak Temperature, differentials)
// come with Temperature Only only when the user ticked it: Concrete
// Maturity, TMC and Evaporation add it as a dependency, and a maturity-only
// pour has no mass-concrete limits.
const MASS_CONCRETE_FIELDS = new Set(['massConcreteLimitGroup', 'temperatureLimits', 'temperatureDifferentials']);

// ─── Location form state ───
type HardwareType = 'ConXagent' | 'ConXsensors' | 'ConXone' | 'Weather Station' | 'Temperature & Humidity' | 'TMC' | '';

/** Hardware with a single channel: no channel to pick, always 1. Mirrors
 *  PourLocationsSection. */
const isSingleChannelHardware = (hw: HardwareType | ''): boolean => hw === 'ConXsensors' || hw === 'ConXone';

interface LocationFormData {
  LocationID: string;
  LocationDescription: string;
  HardwareType: HardwareType;
  LoggerID: string;
  ChannelNo: number;
  MonitorTemperature: boolean;
  CalculateMaturity: boolean;
  /** Temperature Only (ambient): a concrete-hardware channel reading the
   *  air. Never with CalculateMaturity. */
  IsAmbient: boolean;
  DiffLocationIndices: number[];
  CalculateEvaporation: boolean;
  CalculateTMC: boolean;
  ApplyTemperatureLimits: boolean;
  LogginStart: string;
  EAStart: string;
  LoggingEnd: string;
  EvaporationStopTime: string;
  hasWeather: boolean;
  TankID: string;
}

const HARDWARE_TYPE_OPTIONS: { value: HardwareType; label: string }[] = [
  { value: 'ConXagent', label: 'ConXagent' },
  { value: 'ConXsensors', label: 'ConXsensor' },
  { value: 'ConXone', label: 'ConXone' },
  { value: 'TMC', label: 'Match\nTank' },
  { value: 'Weather Station', label: 'Weather\nStation' },
  { value: 'Temperature & Humidity', label: 'Temp &\nHumidity' },
];

const HARDWARE_TYPE_TO_LOGGER_TYPE: Record<HardwareType, string[]> = {
  'ConXagent': ['01'],
  'ConXsensors': ['02'],
  'ConXone': ['03'],
  'Weather Station': ['07'],
  // '09' = Digit Sensor (legacy single-channel T&H), '10' = ConXenviro
  // (4 ports: DS18B20 probe on ch1, SHT40 temp on ch3, SHT40 humidity on ch4)
  'Temperature & Humidity': ['09', '10'],
  'TMC': ['08'],
  '': [],
};

// Which hardware types each monitoring function works with. A type is offered
// when ANY selected function allows it; with no functions selected every type
// shows. Mirrors PourLocationsSection.
const FUNCTION_HARDWARE: Record<string, HardwareType[]> = {
  'Temperature Only': ['ConXagent', 'ConXsensors', 'ConXone'],
  'Concrete Maturity': ['ConXagent', 'ConXsensors', 'ConXone'],
  'Temperature Match Curing': ['ConXagent', 'ConXsensors', 'ConXone', 'TMC'],
  'Calculate Concrete Evaporation Rate': ['ConXagent', 'ConXsensors', 'ConXone', 'Weather Station'],
  'Onsite Weather Conditions': ['Weather Station'],
  'External Temperature and Humidity': ['Temperature & Humidity'],
};

const allowedHardwareTypes = (functions: string[]): Set<HardwareType> => {
  if (!functions.length) {
    return new Set(HARDWARE_TYPE_OPTIONS.map((o) => o.value));
  }
  const allowed = new Set<HardwareType>();
  functions.forEach((f) => (FUNCTION_HARDWARE[f] ?? []).forEach((h) => allowed.add(h)));
  return allowed;
};

/** Hardware held by another active pour is unavailable; the row's own current
 *  selection stays visible so existing values still render. */
const deviceAvailable = (
  d: { id: string; in_use_by_pour?: string | null },
  currentId?: string,
): boolean => d.id === currentId || !d.in_use_by_pour;

// Type "99" is the simulator's virtual logger (see backend pour.py /simulate).
// Super admins can assign it under any hardware type to drive simulated data.
const VIRTUAL_LOGGER_TYPE = '99';

const loggerDisplayName = (l: { name: string; type?: string | null } | null | undefined): string | undefined =>
  l ? (l.type === VIRTUAL_LOGGER_TYPE ? 'Virtual Logger (Simulator)' : l.name) : undefined;

/** The name a draft location saves under: createPour sends
 *  LocationID || LocationDescription, so duplicate checks use the same. */
const draftLocationName = (l: { LocationID: string; LocationDescription: string }): string =>
  l.LocationID || l.LocationDescription;

/** Hardware that measures the site, not the concrete (a weather station or
 *  an ambient temperature & humidity device): it never computes maturity
 *  and has no concrete start. */
const isSiteHardware = (hw: HardwareType): boolean =>
  hw === 'Weather Station' || hw === 'Temperature & Humidity';

/** A draft row reading concrete, so it can own a temperature differential:
 *  not site hardware and not a channel reading the air. The backend drops
 *  pairs owned by those rows. */
const isConcreteDraft = (l: { HardwareType: HardwareType; IsAmbient?: boolean }): boolean =>
  !isSiteHardware(l.HardwareType) && !l.IsAmbient;

/** Most draft problems listed at once before the rest are summarised. */
const MAX_DRAFT_PROBLEMS = 5;

const defaultLocationForm: LocationFormData = {
  LocationID: '',
  LocationDescription: '',
  HardwareType: '',
  LoggerID: '',
  ChannelNo: 1,
  MonitorTemperature: true,
  CalculateMaturity: false,
  IsAmbient: false,
  DiffLocationIndices: [],
  CalculateEvaporation: false,
  CalculateTMC: false,
  ApplyTemperatureLimits: false,
  LogginStart: '',
  EAStart: '',
  LoggingEnd: '',
  EvaporationStopTime: '',
  hasWeather: false,
  TankID: '',
};

const PourCreatePage: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const routerLocation = useLocation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const { user } = useAuth();
  // Inline "add new" buttons follow the matching create permission.
  const canAddProject = hasPermission(user, 'projects.create');
  const canAddProduct = hasPermission(user, 'mix_designs.create');
  const canAddPlant = hasPermission(user, 'batch_plants.create');
  // The maturity panel links to the mix design page (new tab) to fix its
  // maturity inputs, calibration and targets.
  const canViewMixDesign = hasPermission(user, 'mix_designs.view');
  const superAdmin = isSuperAdmin(user);

  // The concrete start (EAStart) has one name on the pour pages, with what
  // it does (same keys as the edit page's locations).
  const concreteStartLabel = t('pourLocations.concreteStart', 'Concrete start (maturity counted from)');
  const concreteStartHelp = t(
    'pourLocations.concreteStartHelp',
    'Maturity is counted from this time. Set it to when the concrete was placed around the sensor.',
  );

  // Duplicate mode: /pours/new opened from a pour's Duplicate action —
  // everything prefills from the source pour; the user adjusts the pour
  // date, name and logger start times (UI Development doc 27.07).
  const duplicateFromPourId: string | undefined =
    (routerLocation.state as any)?.duplicateFromPourId;
  const [duplicateSourceLabel, setDuplicateSourceLabel] = useState<string>('');
  const duplicateLoadedRef = useRef(false);

  // ─── Wizard state ───
  const [activeStep, setActiveStep] = useState(0);
  const [furthestStep, setFurthestStep] = useState(0);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string>('');
  const [success, setSuccess] = useState<string>('');

  // ─── Step 1: Function ───
  const [selectedFunctions, setSelectedFunctions] = useState<MonitoringFunctionId[]>([]);
  const [manuallySelected, setManuallySelected] = useState<MonitoringFunctionId[]>([]);
  // Effective pour-type access (company licensing ∩ per-user restriction),
  // fetched fresh from /auth/me so it can't go stale in localStorage.
  // null = still loading → everything selectable until we know better;
  // the backend re-validates on submit either way.
  const [allowedFunctions, setAllowedFunctions] = useState<MonitoringFunctionId[] | null>(null);
  useEffect(() => {
    authAPI
      .getCurrentUser()
      .then((me) => {
        if (me.allowed_monitoring_functions) {
          setAllowedFunctions(me.allowed_monitoring_functions as MonitoringFunctionId[]);
        }
      })
      .catch(() => {
        // Leave null (no client-side gating); server still enforces.
      });
  }, []);
  const isFunctionAllowed = (funcId: MonitoringFunctionId): boolean =>
    allowedFunctions === null || allowedFunctions.includes(funcId);

  // ─── Step 2: Pour Details ───
  const [pourName, setPourName] = useState('');
  const [projectId, setProjectId] = useState('');
  const [location, setLocation] = useState('');
  const [pourVolume, setPourVolume] = useState('');
  const [projectRef, setProjectRef] = useState('');
  // In the browser's own timezone until a project is picked (see startZone).
  const [pourStartTime, setPourStartTime] = useState(() => defaultPourStart());
  // Set once the user picks a start: the default is then left alone.
  const [pourStartEdited, setPourStartEdited] = useState(false);
  const [pourFinishTime, setPourFinishTime] = useState('');
  const [xHours, setXHours] = useState('28');
  const [stopType, setStopType] = useState('31');
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [levels, setLevels] = useState('');
  const [startAllSensors, setStartAllSensors] = useState(true);
  const [stagedFiles, setStagedFiles] = useState<File[]>([]);
  const [imagePreview, setImagePreview] = useState<{ src: string; name: string } | null>(null);
  // Object URLs for image thumbnails in the staged-attachments list (null for non-images)
  const stagedPreviews = useMemo(
    () => stagedFiles.map((f) => (f.type.startsWith('image/') ? URL.createObjectURL(f) : null)),
    [stagedFiles]
  );
  useEffect(() => {
    return () => {
      stagedPreviews.forEach((url) => {
        if (url) URL.revokeObjectURL(url);
      });
    };
  }, [stagedPreviews]);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Product selection
  const [allProducts, setAllProducts] = useState<ProductListItem[]>([]);
  const [selectedProduct, setSelectedProduct] = useState<ProductListItem | null>(null);

  // Collapse per-revision rows to the newest revision; keep the current
  // selection in the list so a pre-selected older revision still resolves.
  const productOptions = useMemo(() => {
    const options = collapseProductRevisions(allProducts, formatProductOption);
    if (selectedProduct && !options.some((p) => p.product_id === selectedProduct.product_id)) {
      options.push(selectedProduct);
    }
    return options;
  }, [allProducts, selectedProduct]);
  const [productDetail, setProductDetail] = useState<Product | null>(null);
  // Strength grades are in the company's unit (StrengthFormat): the selected
  // mix design's company, and the user's company for a new mix design. No
  // unit is shown until it is known.
  const [productStrengthUnit, ownStrengthUnit] = useCompanyStrengthUnits(
    selectedProduct?.company_id,
    user?.CompanyID,
  );
  const [supplierPlantId, setSupplierPlantId] = useState<string>('');
  // The selected mix design's calibration links (for the calibration's code).
  const [calibrationLinks, setCalibrationLinks] = useState<ProductCalibrationLink[]>([]);
  // The mix design whose calibration links have loaded, and the latest pick,
  // so a slower earlier load is dropped.
  const [calibrationsLoadedFor, setCalibrationsLoadedFor] = useState<string | null>(null);
  const productPickRef = useRef<string | null>(null);
  // The mix design is being read again (Reload mix design).
  const [reloadingProduct, setReloadingProduct] = useState(false);

  // ASTM C1074 9.2: the pour must compute maturity on the calibration's basis.
  // The server picks the calibration (the mix design's active, finished one
  // with a usable curve, the newest of several), so it is asked with none and
  // its answer names the calibration the pour gets.
  const calibrationsLoaded =
    !!selectedProduct && calibrationsLoadedFor === selectedProduct.product_id;
  // The rule holds the save only for a pour that computes maturity (Concrete
  // Maturity); for any other pour the check is information. A refusal from
  // the server counts whatever the page thought, unless it refused a
  // maturity pour that no longer computes maturity.
  const computesMaturity = pourComputesMaturity(monitoringFunctionsToString(selectedFunctions), null);
  const calibrationCheck = useCalibrationCheck(
    selectedProduct?.product_id,
    null,
    calibrationsLoaded,
    computesMaturity,
  );
  const pourCalibrationId = calibrationCheck.check?.calibration_id || null;
  const selectedCalibration = pourCalibrationId
    ? calibrationLinks.find((l) => l.calibration_id === pourCalibrationId) ?? null
    : null;
  const calibrationLabel = selectedCalibration
    ? [selectedCalibration.calibration_code, selectedCalibration.calibration_name].filter(Boolean).join(' · ')
    : calibrationCheck.check?.calibration_name || '';
  // No active, finished link means the server has none either; a link the
  // server did not pick has no usable curve.
  const noCalibration = calibrationsLoaded
    ? noCalibrationText(calibrationLinks, calibrationCheck.check)
    : null;
  // What the pour will compute with: the mix design's maturity group (the
  // backend copies it onto the pour), else the server's planned basis (the
  // ParamInfo defaults). Never the calibration's value.
  const plannedBasis = productDetail?.maturity_method ? null : calibrationCheck.check?.pour_basis ?? null;
  const plannedArrhenius = !!plannedBasis && plannedBasis.method !== 'Nurse Saul';
  const activationEnergyDisplay: number | null = productDetail?.maturity_method
    ? productDetail.maturity_activation_energy ?? null
    : plannedArrhenius ? plannedBasis?.activation_energy ?? null : null;
  const qDisplay: number | null = productDetail?.maturity_method
    ? productDetail.maturity_q ?? null
    : plannedArrhenius ? plannedBasis?.q_value ?? null : null;
  const basisEnforced = computesMaturity || calibrationCheck.refusalHolds;
  const checkStatus = calibrationCheck.check?.status;
  // Why saving must wait (mismatch, or differences not yet confirmed).
  const basisBlock = basisEnforced
    ? calibrationCheckBlock(calibrationCheck.check, calibrationCheck.acknowledged)
    : null;
  // From Pour Details on (where the mix design is picked) the wizard's
  // SAVE / NEXT waits for it.
  const primaryBlocked = activeStep >= 1 && !!basisBlock;
  // Shown in the maturity panel of a maturity pour, and on any pour whose
  // basis differs, so it is never a surprise (as information when the pour
  // does not compute maturity).
  const showCalibrationCheck =
    !!selectedProduct &&
    (computesMaturity || !!basisBlock || checkStatus === 'differs' || checkStatus === 'mismatch');
  const calibrationCheckPanel = (
    <CalibrationCheckPanel
      check={calibrationCheck.check}
      loading={calibrationCheck.loading}
      failed={calibrationCheck.failed}
      noCalibrationText={noCalibration}
      informational={!basisEnforced}
      acknowledged={calibrationCheck.acknowledged}
      onAcknowledgeChange={calibrationCheck.setAcknowledged}
    />
  );
  // Past Pour Details (where the panel sits), a check to settle is shown on
  // each step too, and stays after its box is ticked.
  const laterStepCheck =
    activeStep >= 2 &&
    !!selectedProduct &&
    basisEnforced &&
    (checkStatus === 'differs' || checkStatus === 'mismatch');

  // Limit group selections
  const [targetGroupId, setTargetGroupId] = useState('');
  const [limitGroupId, setLimitGroupId] = useState('');
  const [evaLimitGroupId, setEvaLimitGroupId] = useState('');
  const [productTargetGroups, setProductTargetGroups] = useState<TargetGroup[]>([]);
  // The target group's targets shown on the review: null while loading or
  // without a group, 'failed' when they could not be read.
  const [reviewTargets, setReviewTargets] = useState<DefaultTarget[] | 'failed' | null>(null);

  // ─── Step 3: Locations ───
  const [locations, setLocations] = useState<LocationFormData[]>([]);
  const [locationDialogOpen, setLocationDialogOpen] = useState(false);
  const [editingLocationIdx, setEditingLocationIdx] = useState<number | null>(null);
  const [locationForm, setLocationForm] = useState<LocationFormData>(defaultLocationForm);
  // The dialog's error list shows from the first Save that finds a problem
  // until the dialog is opened again (see shownLocationErrors).
  const [locationErrorsShown, setLocationErrorsShown] = useState(false);
  const [datePopover, setDatePopover] = useState<{
    anchorEl: HTMLElement | null;
    locIdx: number;
    field: 'LogginStart' | 'EAStart' | 'LoggingEnd';
  } | null>(null);

  // ─── Step 4: Contacts ───
  const [contacts, setContacts] = useState<{ contactId: string; sendEmail: boolean; alert: boolean }[]>([]);
  const [contactDialogOpen, setContactDialogOpen] = useState(false);
  const [newContactId, setNewContactId] = useState('');

  // ─── Unsaved entries ───
  // What counts as entered: a function picked; any Pour Details field typed
  // or picked (name, project, location, reference, volume, levels, mix
  // design, batch plant, groups, weather station, attachments); a Pour Start
  // the user picked, a finish time, or stop or start-all settings changed
  // from their defaults; a draft location or a contact. A duplicate counts
  // once its copy has loaded. The default Pour Start the page fills in by
  // itself does not count, and nothing counts once the pour is created.
  const hasUnsavedEntries =
    !success &&
    (!!duplicateSourceLabel ||
      selectedFunctions.length > 0 ||
      [pourName, location, projectRef, pourVolume, levels].some((v) => v.trim() !== '') ||
      !!projectId ||
      !!selectedProduct ||
      !!supplierPlantId ||
      !!targetGroupId ||
      !!limitGroupId ||
      !!evaLimitGroupId ||
      stagedFiles.length > 0 ||
      pourStartEdited ||
      !!pourFinishTime ||
      stopType !== '31' ||
      xHours !== '28' ||
      !startAllSensors ||
      locations.length > 0 ||
      contacts.length > 0);
  // Cancel / Return asks before leaving with unsaved entries.
  const [leaveConfirmOpen, setLeaveConfirmOpen] = useState(false);
  // Set by Leave without saving, so the move it makes is not asked about again.
  const leaveConfirmedRef = useRef(false);
  // A sidebar link and the browser's Back / Forward ask the same question
  // (a move that stays on this page is let through).
  const shouldBlockLeave = useCallback<BlockerFunction>(
    ({ currentLocation, nextLocation }) =>
      hasUnsavedEntries &&
      !leaveConfirmedRef.current &&
      currentLocation.pathname !== nextLocation.pathname,
    [hasUnsavedEntries],
  );
  const leaveBlocker = useBlocker(shouldBlockLeave);
  // A move asked for while the pour was saving goes ahead once it is
  // created: nothing is lost by then.
  useEffect(() => {
    if (leaveBlocker.state === 'blocked' && !hasUnsavedEntries) leaveBlocker.proceed();
  }, [leaveBlocker, hasUnsavedEntries]);
  const leaveDialogOpen = leaveConfirmOpen || leaveBlocker.state === 'blocked';
  const keepEditing = () => {
    setLeaveConfirmOpen(false);
    if (leaveBlocker.state === 'blocked') leaveBlocker.reset();
  };
  const leaveWithoutSaving = () => {
    leaveConfirmedRef.current = true;
    setLeaveConfirmOpen(false);
    // The blocked move goes ahead; from Cancel / Return, the pour list.
    if (leaveBlocker.state === 'blocked') leaveBlocker.proceed();
    else navigate('/pours');
  };

  // Opens the new pour's dashboard a moment after it is created; dropped if
  // the page was left first. A save can finish after the page was left
  // (Leave now while saving), so the move is only set up while it is open.
  const dashboardTimerRef = useRef<number | undefined>(undefined);
  const pageOpenRef = useRef(true);
  useEffect(() => {
    // Set again on mount: StrictMode's trial unmount would leave it false.
    pageOpenRef.current = true;
    return () => {
      pageOpenRef.current = false;
      window.clearTimeout(dashboardTimerRef.current);
    };
  }, []);

  // A refresh or a closed tab would lose them too, so the browser asks first.
  useEffect(() => {
    if (!hasUnsavedEntries) return undefined;
    const warn = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      // Older browsers ask only when returnValue is set.
      e.returnValue = '';
    };
    window.addEventListener('beforeunload', warn);
    return () => window.removeEventListener('beforeunload', warn);
  }, [hasUnsavedEntries]);

  // ─── Lookup data ───
  const [lookups, setLookups] = useState<PourLookups | null>(null);
  const [lookupLoading, setLookupLoading] = useState(true);
  const [supplierPlants, setSupplierPlants] = useState<SupplierPlant[]>([]);

  // ─── Load lookup data + products upfront ───
  const loadLookupData = useCallback(async () => {
    setLookupLoading(true);
    const errors: string[] = [];

    await Promise.allSettled([
      poursAPI.getLookups()
        .then((data) => setLookups(data))
        .catch((err) => { console.error('Failed to load lookups:', err); errors.push('Lookups'); }),
      productsService.listProducts(undefined, true)
        .then((res) => setAllProducts(Array.isArray(res) ? res : []))
        .catch((err) => { console.error('Failed to load products:', err); errors.push('Products'); }),
      productsService.listSupplierPlants(undefined, true)
        .then((res) => setSupplierPlants(Array.isArray(res) ? res : []))
        .catch(() => {}),
    ]);

    if (errors.length > 0) {
      setError(`Failed to load: ${errors.join(', ')}. Some dropdowns may be empty.`);
    }
    setLookupLoading(false);
  }, []);

  useEffect(() => {
    loadLookupData();
  }, [loadLookupData]);

  // ─── Duplicate mode: prefill everything from the source pour ───
  useEffect(() => {
    if (!duplicateFromPourId || lookupLoading || duplicateLoadedRef.current) return;
    duplicateLoadedRef.current = true;
    // Transient network hiccups shouldn't strand the user on an empty
    // wizard — retry each fetch briefly before surfacing an error.
    const withRetry = async <T,>(fn: () => Promise<T>, attempts = 3): Promise<T> => {
      let lastErr: unknown;
      for (let i = 0; i < attempts; i++) {
        try {
          return await fn();
        } catch (e) {
          lastErr = e;
          await new Promise((r) => setTimeout(r, 600));
        }
      }
      throw lastErr;
    };
    (async () => {
      try {
        const [detail, locs, pourContacts] = await Promise.all([
          withRetry(() => poursAPI.getPourDetail(duplicateFromPourId)),
          withRetry(() => poursAPI.getPourLocations2(duplicateFromPourId)).catch(() => []),
          withRetry(() => poursAPI.getPourContacts(duplicateFromPourId)).catch(() => []),
        ]);
        setDuplicateSourceLabel(detail.PourCode || detail.PourName || duplicateFromPourId);

        // Step 1 — functions
        const funcs = (detail.MonitoringFunctions || '')
          .split(',')
          .map((s: string) => s.trim())
          .filter((s: string): s is MonitoringFunctionId =>
            FUNCTION_OPTIONS.some((opt) => opt.id === s));
        // The stored list does not say which functions were ticked and which
        // came as dependencies. Temperature Only that another function needs
        // counts as ticked (with its mass-concrete controls) only when the
        // source pour used them: a limit group, Peak Temperature or a pair.
        const needsTemperatureOnly = funcs.some((f) =>
          (FUNCTION_DEPENDENCIES[f] || []).includes('Temperature Only'));
        const usedMassConcrete =
          !!detail.LimitGroupID ||
          locs.some((l) => !!l.has_temp_limit || (l.temp_diff_location_ids || []).length > 0);
        setManuallySelected(
          needsTemperatureOnly && !usedMassConcrete
            ? funcs.filter((f) => f !== 'Temperature Only')
            : funcs,
        );
        setSelectedFunctions(funcs);

        // Step 2 — pour details. The pour date defaults to "now" and the
        // name is suffixed so the user consciously renames it.
        setPourName(detail.PourName ? `${detail.PourName} - Copy` : '');
        setProjectId(detail.ProjectID || '');
        setLocation(detail.Location || '');
        setProjectRef(detail.ProjectRef || '');
        setPourVolume(detail.PourVolume != null ? String(detail.PourVolume) : '');
        setLevels(detail.Levels || '');
        // A new pour only stops after a number of days: the copied pour's
        // days carry over, any other way to stop starts from 28 days.
        if (detail.StopType === STOP_AFTER_DAYS && detail.XHours != null) {
          setXHours(String(detail.XHours));
        }
        // The weather station is picked on the hardware step.

        // Product + its dependent groups; the source pour's own group
        // selections take precedence over the product defaults.
        const product = allProducts.find((p) => p.product_id === detail.ProductID) || null;
        const groups = product ? await handleProductSelect(product) : [];
        // Only a group the mix design still has (one deleted since is left out).
        if (detail.TargetGroupID && groups.some((g) => g.target_group_id === detail.TargetGroupID)) {
          setTargetGroupId(detail.TargetGroupID);
        }
        if (detail.LimitGroupID) setLimitGroupId(detail.LimitGroupID);
        if (detail.EvaLimitGroupID) setEvaLimitGroupId(detail.EvaLimitGroupID);
        if (detail.SupplierPlantID) setSupplierPlantId(detail.SupplierPlantID);

        // Step 3 — locations. Loggers/channels/flags copy over; the logging
        // times are cleared so the user sets fresh start times.
        const loggerTypeById = new Map(
          (lookups?.loggers || []).map((l) => [l.id, l.type]),
        );
        const hwFromLoggerType = (type?: string | null): HardwareType => {
          if (type === '02') return 'ConXsensors';
          if (type === '03') return 'ConXone';
          if (type === '07') return 'Weather Station';
          if (type === '08') return 'TMC';
          if (type === '09' || type === '10') return 'Temperature & Humidity';
          return 'ConXagent';
        };
        // The saved name goes in LocationDescription with LocationID left
        // blank, as for any new draft: the table shows and renames
        // LocationDescription, and createPour sends LocationID first.
        const drafts: LocationFormData[] = locs.map((l) => {
          const hardware: HardwareType = l.TankID ? 'TMC' : hwFromLoggerType(loggerTypeById.get(l.LoggerID));
          return {
            ...defaultLocationForm,
            LocationID: '',
            LocationDescription: l.LocationID || l.LocationDescription || '',
            HardwareType: hardware,
            LoggerID: l.LoggerID || '',
            ChannelNo: l.ChannelNo || 1,
            MonitorTemperature: true,
            // Site hardware never computes maturity (an older pour may say so).
            CalculateMaturity: l.MonitorType === '1' && !isSiteHardware(hardware),
            IsAmbient: !!l.IsAmbient && !isSiteHardware(hardware),
            CalculateTMC: !!l.TankID,
            TankID: l.TankID || '',
            hasWeather: !!l.hasWeather,
            // Differential pairs are copied separately (DiffLocationIndices), so a
            // pair alone must not switch on Peak Temperature for the copy.
            ApplyTemperatureLimits: !!l.has_temp_limit,
            DiffLocationIndices: [],
          };
        });
        // Differential partners come back as the partner's location ID
        // (older pours may hold its name instead). Match by ID first, then
        // by name, and store each pair once, on a concrete row.
        const sourceIndexById = new Map<string, number>();
        const sourceIndexByName = new Map<string, number>();
        locs.forEach((l, i) => {
          if (l.ID && !sourceIndexById.has(l.ID)) sourceIndexById.set(l.ID, i);
        });
        [locs.map((l) => l.LocationID), locs.map((l) => l.LocationDescription)].forEach((names) =>
          names.forEach((n, i) => {
            const key = locationNameKey(n);
            if (key && !sourceIndexByName.has(key)) sourceIndexByName.set(key, i);
          }),
        );
        const sourceIndexOf = (ref: string): number | undefined =>
          sourceIndexById.get(ref) ?? sourceIndexByName.get(locationNameKey(ref));
        const pairsSeen = new Set<string>();
        locs.forEach((l, i) => {
          (l.temp_diff_location_ids || []).forEach((ref) => {
            const j = sourceIndexOf(ref);
            if (j === undefined || j === i) return;
            const key = i < j ? `${i}-${j}` : `${j}-${i}`;
            if (pairsSeen.has(key)) return;
            pairsSeen.add(key);
            const owner = isConcreteDraft(drafts[i]) ? i : isConcreteDraft(drafts[j]) ? j : -1;
            if (owner < 0) return;
            drafts[owner] = {
              ...drafts[owner],
              DiffLocationIndices: [...drafts[owner].DiffLocationIndices, owner === i ? j : i],
            };
          });
        });
        setLocations(drafts);

        // Step 4 — notification contacts
        setContacts(
          pourContacts.map((c) => ({
            contactId: c.ContactID,
            sendEmail: c.SendEmail === 'Y',
            alert: c.Alert === 'Y',
          })),
        );
      } catch (err: any) {
        // Allow a page reload (fresh mount) to retry cleanly.
        duplicateLoadedRef.current = false;
        setError(apiErrorText(err, 'Failed to load the pour to duplicate'));
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [duplicateFromPourId, lookupLoading]);

  // ─── Field visibility helper ───
  // Mass-concrete fields count Temperature Only only when the user ticked
  // it, not when another function added it as a dependency.
  const isFieldVisible = (fieldId: string): boolean => {
    const vis = FIELD_VISIBILITY[fieldId];
    if (!vis) return true;
    const functions = MASS_CONCRETE_FIELDS.has(fieldId)
      ? selectedFunctions.filter((f) => f !== 'Temperature Only' || manuallySelected.includes(f))
      : selectedFunctions;
    return functions.some((f) => vis[f]);
  };

  const HARDWARE_TYPE_FIELDS: Record<HardwareType | '', Record<string, boolean>> = {
    'ConXagent': { channelNumber: true, concreteTemperatureStartTime: true, temperatureLimits: true, temperatureDifferentials: true, monitoringFunctions: true, tank: false },
    'ConXsensors': { channelNumber: true, concreteTemperatureStartTime: true, temperatureLimits: true, temperatureDifferentials: true, monitoringFunctions: true, tank: false },
    'ConXone': { channelNumber: true, concreteTemperatureStartTime: true, temperatureLimits: true, temperatureDifferentials: true, monitoringFunctions: true, tank: false },
    'Weather Station': { channelNumber: false, concreteTemperatureStartTime: false, temperatureLimits: false, temperatureDifferentials: false, monitoringFunctions: false, tank: false },
    // channelNumber stays hidden: users never pick channels for a T&H device —
    // saving a ConXenviro location silently creates one location per real
    // channel (see saveLocation's enviro expansion).
    'Temperature & Humidity': { channelNumber: false, concreteTemperatureStartTime: false, temperatureLimits: false, temperatureDifferentials: false, monitoringFunctions: false, tank: false },
    'TMC': { channelNumber: true, concreteTemperatureStartTime: true, temperatureLimits: false, temperatureDifferentials: false, monitoringFunctions: true, tank: true },
    '': { channelNumber: true, concreteTemperatureStartTime: true, temperatureLimits: true, temperatureDifferentials: true, monitoringFunctions: true, tank: true },
  };

  const isLocFieldVisible = (fieldId: string): boolean => {
    const hwFields = HARDWARE_TYPE_FIELDS[locationForm.HardwareType];
    if (hwFields && hwFields[fieldId] === false) return false;
    return isFieldVisible(fieldId);
  };

  // Hardware types the selected monitoring functions support; the form's
  // current type stays visible so an existing selection still renders.
  const dialogHardwareOptions = useMemo(() => {
    const allowed = allowedHardwareTypes(selectedFunctions);
    if (locationForm.HardwareType) allowed.add(locationForm.HardwareType);
    return HARDWARE_TYPE_OPTIONS.filter((o) => allowed.has(o.value));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedFunctions, locationForm.HardwareType]);

  // Only tanks not held by another active pour (current selection kept).
  const availableTanks = (currentId?: string) =>
    (lookups?.tanks ?? []).filter((t) => deviceAvailable(t, currentId));

  // ─── Silent multi-sensor expansion (ConXenviro) ───
  // A ConXenviro carries several sensors (temp probe + SHT40 temp/humidity).
  // The dialog stays a simple pick-device-and-name form; saveLocation expands
  // the single entry into one location per real channel automatically.
  const dialogLogger = (lookups?.loggers ?? []).find((l) => l.id === locationForm.LoggerID);
  const channelTypeLabel = (typ?: string | null): string =>
    typ === 'HUMIDITY' ? 'Humidity' : 'Temperature';
  /** Real channels an enviro Add expands into: populated + typed and not
   *  already bound by another location on the same logger. Empty for
   *  non-enviro loggers or while editing a single existing location. */
  const enviroExpandChannels =
    editingLocationIdx === null &&
    locationForm.HardwareType === 'Temperature & Humidity' &&
    dialogLogger?.type === '10'
      ? (dialogLogger.channels ?? []).filter(
          (c) =>
            c.populated &&
            c.type &&
            !locations.some(
              (loc) => loc.LoggerID === locationForm.LoggerID && loc.ChannelNo === c.no,
            ),
        )
      : [];

  /** Whether a draft location can be the other side of a temperature
   *  differential: not a weather station (no track data) or a humidity
   *  channel. Same rule as the edit page (PourLocationsSection). */
  const canBeDiffPartner = (loc: LocationFormData): boolean => {
    if (loc.HardwareType === 'Weather Station') return false;
    const ch = (lookups?.loggers ?? [])
      .find((l) => l.id === loc.LoggerID)
      ?.channels?.find((c) => c.no === loc.ChannelNo);
    return (ch?.type || '').toUpperCase() !== 'HUMIDITY';
  };

  /** Locations a concrete location may pair with: never itself. */
  const diffPartners = locations
    .map((loc, idx) => ({ loc, idx }))
    .filter(({ loc, idx }) => idx !== editingLocationIdx && canBeDiffPartner(loc));

  // ─── Differentials column (table and mobile cards) ───
  // Pairs are read from both sides (symmetricPartners) and written on a
  // concrete owner (setPartners), by draft index.
  const showDiffColumn = isFieldVisible('temperatureDifferentials');
  // Per-location switches in the table, for the functions the pour has.
  const showTempLimitColumn = isFieldVisible('temperatureLimits');
  const showEvaporationColumn = selectedFunctions.includes('Calculate Concrete Evaporation Rate');

  // Weather stations send readings only to a pour that uses weather, and to
  // one station per pour (the server links one of them as the pour's
  // weather station).
  const stationCount = locations.filter((l) => l.HardwareType === 'Weather Station').length;
  const usesWeather =
    selectedFunctions.includes('Onsite Weather Conditions') ||
    selectedFunctions.includes('Calculate Concrete Evaporation Rate');
  const stationNotes: string[] = [];
  if (stationCount > 0 && !usesWeather) {
    stationNotes.push(t(
      'pourCreate.stationNotLinked',
      'A weather station sends readings to a pour only when Onsite Weather Conditions or Concrete Evaporation Rate is turned on.',
    ));
  } else if (stationCount > 1) {
    stationNotes.push(t(
      'pourCreate.oneStationFeeds',
      'Only one weather station sends weather to a pour. The dashboard shows the others as not linked.',
    ));
  }
  // Edit, name, function, start, end and delete, plus the optional columns;
  // the device and empty-table rows span them all.
  const locationTableColumns =
    6 +
    ['channelNumber', 'concreteTemperatureStartTime', 'tank'].filter((f) => isFieldVisible(f)).length +
    (showTempLimitColumn ? 1 : 0) +
    (showEvaporationColumn ? 1 : 0) +
    (showDiffColumn ? 1 : 0);
  // The table lists each device once, with its locations (channels) under
  // it in channel order; devices go in hardware-number order. Rows keep
  // their draft index, which the table's edits and pairs use.
  const draftDeviceGroups = (() => {
    const groups = new Map<string, { key: string; hw: HardwareType; deviceName: string; rows: { loc: LocationFormData; idx: number }[] }>();
    locations.forEach((loc, idx) => {
      const key = loc.LoggerID || `draft-${idx}`;
      let group = groups.get(key);
      if (!group) {
        const logger = lookups?.loggers.find((l) => l.id === loc.LoggerID);
        group = { key, hw: loc.HardwareType, deviceName: loggerDisplayName(logger) || loc.LoggerID || 'No device', rows: [] };
        groups.set(key, group);
      }
      group.rows.push({ loc, idx });
    });
    const list = Array.from(groups.values());
    list.forEach((g) => g.rows.sort((x, y) => Number(x.loc.ChannelNo) - Number(y.loc.ChannelNo)));
    return list.sort((x, y) => x.deviceName.localeCompare(y.deviceName, undefined, { numeric: true }));
  })();
  const draftDiffLabel = (i: number): string =>
    normalizeLocationName(draftLocationName(locations[i])) || `Location ${i + 1}`;
  const draftDiffPartners = (idx: number): DiffPartnerOption[] =>
    symmetricPartners(locations, idx).map((i) => ({ id: String(i), label: draftDiffLabel(i) }));
  const draftDiffOptions = (idx: number): DiffPartnerOption[] =>
    locations
      .map((loc, i) => ({ loc, i }))
      .filter(({ loc, i }) => i !== idx && canBeDiffPartner(loc))
      .map(({ i }) => ({ id: String(i), label: draftDiffLabel(i) }));
  const changeDraftDiffs = (idx: number, ids: string[]) =>
    setLocations((prev) => setPartners(prev, idx, ids.map(Number), (i) => isConcreteDraft(prev[i])));
  const renderDraftDiffs = (loc: LocationFormData, idx: number, variant: 'cell' | 'inline') => {
    const concrete = isConcreteDraft(loc);
    return (
      <DifferentialsCell
        partners={draftDiffPartners(idx)}
        options={concrete ? draftDiffOptions(idx) : []}
        editable={concrete}
        emptyText={concrete ? 'None' : '-'}
        onChange={(ids) => changeDraftDiffs(idx, ids)}
        variant={variant}
      />
    );
  };

  /** The location names an enviro Add will create for a given base name —
   *  shared by the dialog preview and saveLocation so they always agree. */
  const enviroExpandNames = (base: string): { no: number; name: string }[] => {
    const typeCounts = enviroExpandChannels.reduce<Record<string, number>>((acc, c) => {
      const label = channelTypeLabel(c.type);
      acc[label] = (acc[label] || 0) + 1;
      return acc;
    }, {});
    return enviroExpandChannels.map((c) => {
      const label = channelTypeLabel(c.type);
      const suffix = typeCounts[label] > 1 ? `${label} (Ch ${c.no})` : label;
      return { no: c.no, name: `${base} ${suffix}` };
    });
  };

  // ─── Add every channel of a ConXagent at once ───
  // Adding a ConXagent shows all its channels (ChannelRowsPicker): a name
  // per channel, and each ticked channel becomes its own draft location. Editing a single draft location keeps the one-channel
  // form.
  const multiMode =
    editingLocationIdx === null &&
    isMultiChannelHardware(locationForm.HardwareType) &&
    isLocFieldVisible('channelNumber');
  const draftChannelRefs = useMemo<PourChannelRef[]>(
    () =>
      locations.map((l) => ({
        loggerId: l.LoggerID,
        channelNo: l.ChannelNo,
        name: draftLocationName(l),
      })),
    [locations],
  );
  // Top level (not inside the dialog) so the pairs picked between channels
  // survive closing and reopening the dialog.
  const batch = useChannelBatch({
    loggerId: locationForm.LoggerID,
    pourChannels: draftChannelRefs,
    nameScope: locationForm.HardwareType,
    // On a maturity pour each channel picks Temperature Only or Concrete
    // Maturity (legacy: per location).
    maturityAllowed: computesMaturity,
  });
  // The dialog's Function field, and the concrete start it brings: shown
  // for a maturity location, or for any Concrete Maturity channel of a
  // ConXagent being added.
  const dialogMaturityName = describeMaturity(
    productDetail?.maturity_method || plannedBasis?.method || DEFAULT_MATURITY_METHOD,
    productDetail?.maturity_specified_temp ?? plannedBasis?.specified_temp,
  ).index_label;
  const dialogFunctionView = locationFunctionView(locationForm.HardwareType, computesMaturity);
  const dialogConcreteStartVisible =
    isLocFieldVisible('concreteTemperatureStartTime') &&
    (multiMode ? batch.selected.some((r) => r.maturity) : locationForm.CalculateMaturity);
  // "Added 4 locations for AX000734" after Save & add next device, and how
  // many were added since the dialog opened (Cancel becomes Close).
  const [addNotice, setAddNotice] = useState('');
  const [addedCount, setAddedCount] = useState(0);
  const deviceInputRef = useRef<HTMLInputElement>(null);
  const firstNameInputRef = useRef<HTMLInputElement>(null);
  const dialogContentRef = useRef<HTMLDivElement>(null);

  /** Devices the dialog's Hardware Number picker offers: free of other
   *  active pours, matching the hardware type (the simulator's virtual
   *  logger for super admins only), in device-number order. In add mode a
   *  device with every channel already on this pour is left out. The
   *  current selection always stays so the box never shows empty. */
  const dialogDeviceOptions = useMemo<HardwareNumberOption[]>(() => {
    const currentId = locationForm.LoggerID;
    const fullyUsed = new Set(
      editingLocationIdx === null
        ? draftChannelRefs
            .map((r) => r.loggerId)
            .filter((id) =>
              isLoggerFullyUsed(
                draftChannelRefs,
                id,
                isSingleChannelHardware(locationForm.HardwareType) ? [1] : undefined,
              ),
            )
        : [],
    );
    const allowedTypes = HARDWARE_TYPE_TO_LOGGER_TYPE[locationForm.HardwareType];
    return (lookups?.loggers ?? [])
      .filter((l) => {
        if (l.id === currentId) return true;
        if (!deviceAvailable(l, currentId)) return false;
        if (fullyUsed.has(l.id)) return false;
        if (l.type === VIRTUAL_LOGGER_TYPE) return superAdmin;
        if (!allowedTypes || allowedTypes.length === 0) return true;
        if (!l.type) return true;
        return allowedTypes.includes(l.type);
      })
      // Device-number order, same as the edit page's pickers.
      .sort((a, b) => a.name.localeCompare(b.name, undefined, { numeric: true }))
      .map((l) => ({ id: l.id, label: loggerDisplayName(l) || l.name }));
  }, [lookups, locationForm.LoggerID, locationForm.HardwareType, editingLocationIdx, draftChannelRefs, superAdmin]);

  /** Picking a device moves the single-channel form to its first free
   *  channel (always 1 on a single-channel device). In multi mode focus goes
   *  on to the first channel's name. */
  const handleDialogDeviceChange = (newLoggerId: string) => {
    const usedChannels = locations
      .filter((loc, i) => loc.LoggerID === newLoggerId && i !== editingLocationIdx)
      .map((loc) => loc.ChannelNo);
    const nextChannel = isSingleChannelHardware(locationForm.HardwareType)
      ? 1
      : CHANNEL_NUMBERS.find((ch) => !usedChannels.includes(ch)) ?? 1;
    setLocationForm({ ...locationForm, LoggerID: newLoggerId, ChannelNo: nextChannel });
    if (newLoggerId && multiMode) {
      window.setTimeout(() => firstNameInputRef.current?.focus(), 0);
    }
  };

  /** Problems createPour would reject, across every draft location: a blank
   *  or too long name, two rows with the same name (spacing and case
   *  ignored) or two rows on the same device channel. Rows are 1-based, in
   *  table order. */
  const draftLocationProblems = (): string[] => {
    const problems: string[] = [];
    const firstRowByName = new Map<string, number>();
    const firstRowBySlot = new Map<string, number>();
    locations.forEach((loc, i) => {
      const row = i + 1;
      const name = normalizeLocationName(draftLocationName(loc));
      if (!name) {
        problems.push(`Row ${row} has no location name.`);
      } else {
        if (name.length > LOCATION_NAME_MAX) {
          problems.push(`Row ${row} name is longer than ${LOCATION_NAME_MAX} characters.`);
        }
        const key = locationNameKey(name);
        const first = firstRowByName.get(key);
        if (first) {
          problems.push(
            `Rows ${first} and ${row} are both named "${name}". Each location on a pour needs a unique name.`,
          );
        } else {
          firstRowByName.set(key, row);
        }
      }
      if (!loc.LoggerID) return;
      const slot = `${loc.LoggerID}|${Number(loc.ChannelNo)}`;
      const first = firstRowBySlot.get(slot);
      if (first) {
        const device =
          loggerDisplayName((lookups?.loggers ?? []).find((l) => l.id === loc.LoggerID)) || loc.LoggerID;
        const firstName = normalizeLocationName(draftLocationName(locations[first - 1]));
        problems.push(
          `Rows ${first} and ${row} ("${firstName}" and "${name}") both use channel ${loc.ChannelNo} of hardware ${device}. Each channel can only be allocated once per pour.`,
        );
      } else {
        firstRowBySlot.set(slot, row);
      }
    });
    return problems;
  };

  // ─── Handle product selection ───
  // Resolves with the mix design's target groups (none when the load failed
  // or another mix design was picked meanwhile).
  const handleProductSelect = async (product: ProductListItem | null): Promise<TargetGroup[]> => {
    productPickRef.current = product?.product_id ?? null;
    setSelectedProduct(product);
    setProductDetail(null);
    setCalibrationLinks([]);
    setCalibrationsLoadedFor(null);
    setProductTargetGroups([]);
    setTargetGroupId('');
    setLimitGroupId('');
    setEvaLimitGroupId('');

    if (!product) return [];

    try {
      const [detail, calibrations, targetGroups] = await Promise.all([
        productsService.getProduct(product.product_id),
        productsService.listProductCalibrations(product.product_id),
        productsService.listTargetGroups(product.product_id).catch(() => [] as TargetGroup[]),
      ]);
      // Another mix design was picked while this one loaded.
      if (productPickRef.current !== product.product_id) return [];
      setProductDetail(detail);
      // Default the batch plant to the product's linked plant; keep the
      // user's own pick when the product doesn't specify one.
      if (detail.supplier_plant_id) setSupplierPlantId(detail.supplier_plant_id);
      // The calibration itself is the server's pick (the check names it).
      setCalibrationLinks(calibrations);
      setCalibrationsLoadedFor(product.product_id);
      setProductTargetGroups(targetGroups);
      // The mix design's default group only while it still exists: one
      // deleted since stays named on the mix design but has no targets.
      if (detail.target_group_id && targetGroups.some((g) => g.target_group_id === detail.target_group_id)) {
        setTargetGroupId(detail.target_group_id);
      }
      if (detail.mass_concrete_default_id) setLimitGroupId(detail.mass_concrete_default_id);
      if (detail.evaporation_default_id) setEvaLimitGroupId(detail.evaporation_default_id);
      return targetGroups;
    } catch (err) {
      console.error('Failed to load product details:', err);
      return [];
    }
  };

  /** Reads the mix design again after it was changed in its own tab
   *  (maturity inputs, calibration, targets), keeping the user's group
   *  picks that still exist. The calibration is checked again. */
  const reloadProduct = async () => {
    if (!selectedProduct) return;
    const kept = { targetGroupId, limitGroupId, evaLimitGroupId };
    setReloadingProduct(true);
    try {
      const groups = await handleProductSelect(selectedProduct);
      if (kept.targetGroupId && groups.some((g) => g.target_group_id === kept.targetGroupId)) {
        setTargetGroupId(kept.targetGroupId);
      }
      if (kept.limitGroupId) setLimitGroupId(kept.limitGroupId);
      if (kept.evaLimitGroupId) setEvaLimitGroupId(kept.evaLimitGroupId);
    } finally {
      setReloadingProduct(false);
    }
  };

  // ─── Function toggle with dependency logic ───
  const computeSelected = (manual: MonitoringFunctionId[]): MonitoringFunctionId[] => {
    const result: MonitoringFunctionId[] = [];
    for (const id of manual) {
      if (!result.includes(id)) result.push(id);
      for (const dep of (FUNCTION_DEPENDENCIES[id] || []) as MonitoringFunctionId[]) {
        if (!result.includes(dep)) result.push(dep);
      }
    }
    return result;
  };

  const isAutoIncluded = (funcId: MonitoringFunctionId): boolean =>
    selectedFunctions.includes(funcId) && !manuallySelected.includes(funcId);

  const toggleFunction = (funcId: MonitoringFunctionId) => {
    if (!isFunctionAllowed(funcId)) return;
    setManuallySelected((prev) => {
      let next: MonitoringFunctionId[];
      if (prev.includes(funcId)) {
        // Unticking only drops the user's own pick. When another chosen
        // function still needs it, it stays selected as a dependency
        // (Temperature Only then loses its mass-concrete controls) and the
        // functions that need it are kept. A function nothing else needs has
        // no chosen dependents, so nothing else is removed either.
        next = prev.filter((f) => f !== funcId);
      } else {
        // Ticking a function another one added as a dependency makes it the
        // user's own choice (Temperature Only then brings its mass-concrete
        // controls to a maturity pour); the selection itself is unchanged.
        next = [...prev, funcId];
      }
      const computed = computeSelected(next);
      setSelectedFunctions(computed);
      return next;
    });
  };

  // All datetime pickers hold naive wall-clock strings in the project's
  // timezone. The backend stores naive UTC, so convert before sending:
  // otherwise a value entered as e.g. 10:30 AEST gets stored as 10:30 UTC and
  // the dashboard (which renders stored times via the project TZ) shows it
  // shifted by the offset.
  const projectTzIana = lookups?.projects.find((p) => p.id === projectId)?.timezone_iana;
  const toUtc = (v?: string): string | undefined =>
    v ? localInputToUtc(v, projectTzIana) || undefined : undefined;
  // The default Pour Start is worked out again in the picked project's
  // timezone (UTC for a project without one) each time the project changes,
  // until the user picks a start of their own, so it never goes stale.
  // Locations that took the old default take the new one.
  const startZone = projectId ? projectTzIana || 'UTC' : '';
  useEffect(() => {
    if (!startZone || pourStartEdited) return;
    const previous = pourStartTime;
    const next = defaultPourStart(startZone);
    if (next === previous) return;
    setPourStartTime(next);
    setLocations((prev) =>
      prev.some((l) => l.LogginStart === previous || l.EAStart === previous)
        ? prev.map((l) => ({
            ...l,
            LogginStart: l.LogginStart === previous ? next : l.LogginStart,
            EAStart: l.EAStart === previous ? next : l.EAStart,
          }))
        : prev,
    );
    // Only a change of project or zone re-anchors the start.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId, startZone]);
  /** The zone a wall-clock time on this page is in, e.g. "AEDT" (daylight
   *  saving as at that time); "UTC" for a project without a timezone. */
  const zoneOf = (wallClock?: string): string => timeZoneLabel(toUtc(wallClock), projectTzIana);
  /** Picker label naming the zone the time is entered in, e.g. "Pour Start
   *  Time (AEDT)", as on the edit page. No zone until a project is picked:
   *  the times are read in its timezone. */
  const tzLabelled = (label: string, wallClock?: string): string =>
    projectId ? `${label} (${zoneOf(wallClock)})` : label;
  /** A wall-clock time for the tables: "18/10/26 10:30 AEDT". */
  const tableTime = (wallClock?: string): string =>
    wallClock ? `${dayjs(wallClock).format('DD/MM/YY HH:mm')} ${zoneOf(wallClock)}` : '-';
  /** A wall-clock time for the review: "18 Oct 2026, 10:30 AEDT". */
  const reviewTime = (wallClock?: string): string => {
    const utc = toUtc(wallClock);
    return utc ? `${formatDateTimeLabelled(utc, projectTzIana)} ${zoneOf(wallClock)}` : '-';
  };

  // The stop settings as the save would send them: a whole number of days.
  const currentStopErrors = (): Record<string, string> =>
    stopSettingsErrors({ stopType, xHours, presetUtc: '', computesMaturity });
  const stopErrors = currentStopErrors();

  // The review reads the target group's targets each time it opens (and
  // after Reload mix design), so changes made on the mix design page show.
  const onReview = activeStep === REVIEW_STEP;
  const reviewGroupId = isFieldVisible('maturityTargetGroup') ? targetGroupId : '';
  useEffect(() => {
    if (!onReview || !selectedProduct || !reviewGroupId) {
      setReviewTargets(null);
      return undefined;
    }
    let cancelled = false;
    setReviewTargets(null);
    // Promise.resolve().then: a throw while starting the request is handled
    // like a failed request.
    Promise.resolve()
      .then(() => productsService.listTargets(selectedProduct.product_id, reviewGroupId))
      .then((rows) => {
        if (!cancelled) setReviewTargets(Array.isArray(rows) ? rows : []);
      })
      .catch(() => {
        if (!cancelled) setReviewTargets('failed');
      });
    return () => {
      cancelled = true;
    };
  }, [onReview, selectedProduct, reviewGroupId, productDetail]);

  // ─── Validation ───
  // The required Pour Details fields, checked by the step's own Next and
  // again on SAVE (the step can be changed after a jump back from Review).
  const requiredDetailsErrors = (): Record<string, string> => {
    const errs: Record<string, string> = {};
    if (!pourName.trim()) errs.pourName = 'Pour name is required.';
    if (!projectId) errs.projectId = 'Project is required.';
    if (isFieldVisible('productCode') && !selectedProduct) errs.product = 'Product / Mix Design is required.';
    return errs;
  };

  const validateStep = (step: number): boolean => {
    switch (step) {
      case 0:
        if (selectedFunctions.length === 0) {
          setError('Please select at least one monitoring function.');
          return false;
        }
        return true;
      case 1: {
        const errs: Record<string, string> = { ...stopErrors, ...requiredDetailsErrors() };
        setFieldErrors(errs);
        return Object.keys(errs).length === 0;
      }
      case 2:
        return true;
      case 3:
        return true;
      default:
        return true;
    }
  };

  // ─── Step validity (non-mutating, used to drive checkmarks on the stepper) ───
  const isStepValid = (n: number): boolean => {
    switch (n) {
      case 0: return selectedFunctions.length > 0;
      case 1:
        return !!pourName.trim() && !!projectId &&
          (!isFieldVisible('productCode') || !!selectedProduct) &&
          Object.keys(stopErrors).length === 0;
      case 2: return locations.length > 0;
      case 3: return true; // Notifications optional
      default: return false;
    }
  };

  // ─── Navigation ───
  const handleNext = () => {
    setError('');
    if (!validateStep(activeStep)) return;
    if (activeStep === STEPS.length - 1) {
      handleSubmit();
    } else {
      const next = activeStep + 1;
      setActiveStep(next);
      setFurthestStep((prev) => Math.max(prev, next));
    }
  };

  const handleBack = () => {
    setError('');
    setActiveStep((prev) => prev - 1);
  };

  // Cancel / Return: straight back to the pour list when nothing was
  // entered, else ask first.
  const handleCancel = () => {
    if (hasUnsavedEntries) setLeaveConfirmOpen(true);
    else navigate('/pours');
  };

  // Jump straight to an already-visited step (the mobile progress dots, the
  // desktop step labels and the review's Edit links). Forward jumps past
  // the furthest step reached are blocked, and a forward jump checks the
  // current step as its Next would, so required checks are not skipped.
  const goToStep = (target: number) => {
    if (target === activeStep) return;
    if (target > furthestStep) return;
    setError('');
    if (target > activeStep && !validateStep(activeStep)) return;
    setActiveStep(target);
  };

  // ─── Submit ───
  const handleSubmit = async () => {
    // Function and Pour Details are checked by their own Next, but either can
    // change after a jump back from Review and a jump forward past them, so
    // the required checks run again before anything is sent.
    if (!validateStep(0)) {
      setActiveStep(0);
      return;
    }
    const missing = requiredDetailsErrors();
    if (Object.keys(missing).length > 0) {
      setFieldErrors((p) => ({ ...p, ...missing }));
      setError(`Check Pour Details before saving: ${Object.values(missing).join(' ')}`);
      setActiveStep(STEPS.indexOf('Pour Details'));
      return;
    }
    // The stop settings may have changed the same way, or the set time may
    // have passed while the wizard was open. The server would stop such a
    // pour at once.
    const stopProblems = currentStopErrors();
    if (Object.keys(stopProblems).length > 0) {
      setFieldErrors((p) => ({ ...p, ...stopProblems }));
      setError(`Check Stop Monitoring on Pour Details: ${Object.values(stopProblems).join(' ')}`);
      setActiveStep(STEPS.indexOf('Pour Details'));
      return;
    }
    // Catch duplicate names and channels here, with row numbers, rather than
    // as a server 409 after every location has been entered.
    const problems = draftLocationProblems();
    if (problems.length > 0) {
      const shown = problems.slice(0, MAX_DRAFT_PROBLEMS);
      if (problems.length > shown.length) {
        shown.push(`And ${problems.length - shown.length} more.`);
      }
      setError(['Fix these locations on the Hardware / Sensors step before saving:', ...shown].join('\n'));
      return;
    }
    // ASTM C1074 9.2: the server refuses these too; say why before sending.
    if (basisBlock) {
      setError(basisBlock);
      return;
    }
    setSaving(true);
    setError('');
    try {
      const functionalityCode = deriveFunctionalityCode(selectedFunctions);
      const monitoringFunctionsStr = monitoringFunctionsToString(selectedFunctions);
      // The server copies the method from the mix design's maturity group.
      // Without a group the engine computes equivalent age (Arrhenius), so
      // name it so; never a made-up value such as 'equivalent-age'.
      const maturityMethod = productDetail?.maturity_method || 'Arrhenius';

      // Every datetime goes through toUtc (wall clock in the project's
      // timezone to the naive UTC the backend stores).
      const payload: PourCreate = {
        PourName: pourName.trim(),
        ProjectID: projectId,
        // No product on pours without concrete monitoring (e.g. pure
        // External Temperature & Humidity) — backend stores "".
        ProductID: selectedProduct?.product_id ?? '',
        SupplierPlantID: isFieldVisible('supplierPlant') ? (supplierPlantId || undefined) : undefined,
        MaturityMethod: maturityMethod,
        Functionality: functionalityCode,
        MonitoringFunctions: monitoringFunctionsStr,
        StopType: stopType,
        XHours: parseXHours(xHours) ?? undefined,
        Location: location.trim() || undefined,
        ProjectRef: projectRef.trim() || undefined,
        PourVolume: pourVolume ? Number(pourVolume) : undefined,
        Levels: levels.trim() || undefined,
        Started: toUtc(pourStartTime),
        Finished: toUtc(pourFinishTime),
        TargetGroupID: isFieldVisible('maturityTargetGroup') ? (targetGroupId || undefined) : undefined,
        LimitGroupID: isFieldVisible('massConcreteLimitGroup') ? (limitGroupId || undefined) : undefined,
        EvaLimitGroupID: isFieldVisible('evaporationLimitGroup') ? (evaLimitGroupId || undefined) : undefined,
        // The calibration the check was made against (the server's pick), so
        // the pour gets the one the user saw; without a check the server
        // picks it on save.
        CalibrationID: pourCalibrationId || undefined,
        // Only a pour the rule holds is asked to confirm a difference.
        acknowledge_basis_difference: basisEnforced
          ? acknowledgeFlag(calibrationCheck.check, calibrationCheck.acknowledged)
          : undefined,
        // No WeatherStationID: the server takes it from the weather station
        // location picked on the hardware step.
        // No datum temperature, activation energy or specified temperature:
        // the backend copies them from the mix design's maturity input group,
        // and without a group the pour runs on the defaults, which is what the
        // calibration check compared.
        locations:
          locations.length > 0
            ? locations.map((loc) => ({
                LocationID: loc.LocationID || loc.LocationDescription,
                LocationDescription: loc.LocationDescription,
                LoggerID: loc.LoggerID,
                ChannelNo: loc.ChannelNo,
                StartType: '2', // "Start at Date/Time" — matches the edit path
                                // (PourLocationsSection) and legacy behavior. Without
                                // this the location is created with StartType NULL and
                                // never activates (logger stuck at Status "2" waiting).
                // Site hardware never computes maturity and has no
                // concrete start.
                MonitorType: loc.CalculateMaturity && !isSiteHardware(loc.HardwareType) ? '1' : '2',
                LogginStart: toUtc(loc.LogginStart),
                LoggingEnd: toUtc(loc.LoggingEnd),
                EAStart: loc.CalculateMaturity && !isSiteHardware(loc.HardwareType) ? toUtc(loc.EAStart) : undefined,
                hasWeather: loc.CalculateEvaporation || loc.hasWeather,
                TankID: loc.TankID || undefined,
                // Mass-concrete settings only while their controls show
                // (a maturity-only pour has none).
                has_temp_limit:
                  isFieldVisible('temperatureLimits') && loc.ApplyTemperatureLimits && !loc.IsAmbient ? true : undefined,
                // Site hardware is ambient by its type; the flag is for
                // concrete hardware only.
                IsAmbient: !isSiteHardware(loc.HardwareType) && loc.IsAmbient ? true : undefined,
                temp_diff_location_ids: showDiffColumn && loc.DiffLocationIndices.length > 0
                  ? loc.DiffLocationIndices.map((i) => locations[i].LocationID || locations[i].LocationDescription)
                  : undefined,
              }))
            : undefined,
        contacts:
          contacts.length > 0
            ? contacts.map((c) => ({
                ContactID: c.contactId,
                SendEmail: c.sendEmail ? 'Y' : 'N',
                Alert: c.alert ? 'Y' : 'N',
              }))
            : undefined,
      };

      const result = await poursAPI.createPour(payload);

      if (stagedFiles.length > 0) {
        for (const file of stagedFiles) {
          try {
            await poursAPI.uploadPourAttachment(result.PourID, file);
          } catch (uploadErr) {
            console.error('Failed to upload attachment:', file, uploadErr);
          }
        }
      }

      setSuccess('Pour created successfully!');
      if (pageOpenRef.current && !leaveConfirmedRef.current) {
        dashboardTimerRef.current = window.setTimeout(
          () => navigate(`/pours/${result.PourID}/dashboard`, { replace: true }),
          1500,
        );
      }
    } catch (err: any) {
      // A 422 detail is an array; apiErrorText makes it safe to render.
      const text = apiErrorText(err, 'Failed to create pour');
      // A refusal on the calibration rules also shows in the check panel,
      // where it can be settled (confirm the difference, or check again).
      if (err?.response?.status === 422) calibrationCheck.applySaveRefusal(text);
      setError(text);
    } finally {
      setSaving(false);
    }
  };

  // ─── Location dialog ───
  const openAddLocation = () => {
    const hasMaturity = selectedFunctions.includes('Concrete Maturity');
    const hasTMC = selectedFunctions.includes('Temperature Match Curing');
    const hasEvaporation = selectedFunctions.includes('Calculate Concrete Evaporation Rate');
    const hasWeatherFunc = selectedFunctions.includes('Onsite Weather Conditions');
    const hasExtTH = selectedFunctions.includes('External Temperature and Humidity');

    const allowed = allowedHardwareTypes(selectedFunctions);
    let defaultHW: HardwareType = '';
    if (hasTMC) defaultHW = 'TMC';
    else if (hasWeatherFunc) defaultHW = 'Weather Station';
    else if (hasExtTH) defaultHW = 'Temperature & Humidity';
    else defaultHW = 'ConXagent';
    if (!allowed.has(defaultHW)) {
      defaultHW =
        HARDWARE_TYPE_OPTIONS.map((o) => o.value).find((v) => allowed.has(v)) ?? '';
    }

    // The concrete start field is shown whenever the selected functions
    // enable it (e.g. Temperature Only / Mass Concrete) and the default
    // hardware supports it: auto-fill it alongside the monitoring start time.
    const concreteStartVisible =
      isFieldVisible('concreteTemperatureStartTime') &&
      HARDWARE_TYPE_FIELDS[defaultHW]?.concreteTemperatureStartTime !== false;

    setLocationForm({
      ...defaultLocationForm,
      HardwareType: defaultHW,
      MonitorTemperature: true,
      // A maturity pour with External T&H or Onsite Weather opens on site
      // hardware, which never computes maturity (and has no concrete start).
      CalculateMaturity: (hasMaturity || hasTMC) && !isSiteHardware(defaultHW),
      CalculateEvaporation: hasEvaporation,
      CalculateTMC: hasTMC,
      hasWeather: hasEvaporation || hasWeatherFunc,
      LogginStart: startAllSensors && pourStartTime ? pourStartTime : '',
      EAStart: startAllSensors && pourStartTime && concreteStartVisible ? pourStartTime : '',
    });
    setEditingLocationIdx(null);
    setLocationErrorsShown(false);
    // Fresh names, ticks and pour partners; channel pairs stay.
    batch.reset();
    setAddNotice('');
    setAddedCount(0);
    setLocationDialogOpen(true);
  };

  const openEditLocation = (idx: number) => {
    // The chooser shows pairs from both sides, like the table; saving
    // writes them back with setPartners.
    setLocationForm({ ...locations[idx], DiffLocationIndices: symmetricPartners(locations, idx) });
    setEditingLocationIdx(idx);
    setLocationErrorsShown(false);
    setAddNotice('');
    setAddedCount(0);
    setLocationDialogOpen(true);
  };

  /** Picks the dialog's hardware type. Site hardware never computes
   *  maturity and has no concrete start; going back to concrete hardware
   *  restores the pour's maturity default, with the concrete start at the
   *  monitoring start. */
  const changeDialogHardwareType = (hw: HardwareType) => {
    const maturityDefault =
      selectedFunctions.includes('Concrete Maturity') || selectedFunctions.includes('Temperature Match Curing');
    const concreteStartShown =
      isFieldVisible('concreteTemperatureStartTime') &&
      HARDWARE_TYPE_FIELDS[hw]?.concreteTemperatureStartTime !== false;
    setLocationForm((prev) => {
      const site = isSiteHardware(hw);
      return {
        ...prev,
        HardwareType: hw,
        LoggerID: '',
        CalculateMaturity: site ? false : isSiteHardware(prev.HardwareType) ? maturityDefault : prev.CalculateMaturity,
        IsAmbient: site ? false : prev.IsAmbient,
        EAStart: concreteStartShown ? prev.EAStart || prev.LogginStart : '',
      };
    });
  };

  /** What stops the dialog saving, from the form and channel rows as they
   *  are now. It sets no state, so the error list can be worked out again
   *  on every render (shownLocationErrors). */
  const locationDialogProblems = (): string[] => {
    const isEdit = editingLocationIdx !== null;
    const errors: string[] = [];
    if (!multiMode) {
      // Multi mode names each channel in the picker instead (batch.errors).
      const name = normalizeLocationName(locationForm.LocationDescription);
      if (!name) {
        errors.push('Location Name is required.');
      } else {
        // The names this save creates: one per ConXenviro channel, else one.
        const newNames =
          !isEdit && enviroExpandChannels.length > 0
            ? enviroExpandNames(locationForm.LocationDescription.trim()).map((n) => n.name)
            : [draftLocationName(locationForm)];
        // The ConXenviro suffix ("Temperature (Ch 3)") can push a name past
        // the legacy column width.
        const tooLong = newNames.map(normalizeLocationName).find((n) => n.length > LOCATION_NAME_MAX);
        if (tooLong) {
          errors.push(`"${tooLong}" is longer than ${LOCATION_NAME_MAX} characters. Shorten the Location Name.`);
        }
        // An edit is only checked when it changes the name, so an existing
        // clash never blocks an unrelated change.
        const renamed =
          !isEdit ||
          locationNameKey(draftLocationName(locationForm)) !==
            locationNameKey(draftLocationName(locations[editingLocationIdx]));
        const otherNames = locations
          .filter((_, i) => i !== editingLocationIdx)
          .map(draftLocationName);
        const taken = renamed ? newNames.find((n) => isDuplicateLocationName(n, otherNames)) : undefined;
        if (taken) {
          errors.push(`A location named "${normalizeLocationName(taken)}" is already on this pour.`);
        }
      }
    }
    if (!locationForm.HardwareType) errors.push('Hardware Type is required.');
    if (!locationForm.LoggerID) errors.push('Hardware Number is required.');
    if (!locationForm.LogginStart) errors.push('Monitoring Start Time is required.');
    // Only asked for while the field shows (never for site hardware).
    if (dialogConcreteStartVisible && !locationForm.EAStart) {
      errors.push(`${concreteStartLabel} is required when Maturity is enabled.`);
    }
    if (isLocFieldVisible('tank') && !locationForm.TankID.trim()) {
      errors.push('Tank is required for Temperature Match Curing.');
    }
    if (multiMode) errors.push(...batch.errors);
    return errors;
  };

  // The dialog's error list: nothing before the first Save, then worked out
  // again on every change, so a problem the user fixes drops off (and a new
  // one shows) without another Save.
  const shownLocationErrors =
    locationDialogOpen && locationErrorsShown ? locationDialogProblems() : [];

  /** Save the dialog. `addNext` (Save & add next device, or Enter in the
   *  channel rows) keeps the dialog open with its times and settings and
   *  clears only the device, so the next one can be added straight away. */
  const saveLocation = (addNext = false) => {
    const isEdit = editingLocationIdx !== null;
    const errors = locationDialogProblems();
    // Each channel row shows its own error from the first Save on, too.
    if (multiMode) batch.validate();
    if (errors.length > 0) {
      setLocationErrorsShown(true);
      // The list shows at the top of the dialog; bring it into view.
      if (dialogContentRef.current) dialogContentRef.current.scrollTop = 0;
      return;
    }
    setLocationErrorsShown(false);
    let added = 0;
    const diffsShown = isLocFieldVisible('temperatureDifferentials');
    if (editingLocationIdx !== null) {
      const editIdx = editingLocationIdx;
      setLocations((prev) => {
        // The row keeps its own pair list here; setPartners then applies the
        // dialog's partners, which were read from both sides on open.
        const replaced = prev.map((loc, i) =>
          i === editIdx ? { ...locationForm, DiffLocationIndices: loc.DiffLocationIndices } : loc,
        );
        if (!diffsShown) return replaced;
        return setPartners(replaced, editIdx, locationForm.DiffLocationIndices, (i) =>
          isConcreteDraft(replaced[i]),
        );
      });
    } else if (multiMode) {
      // One draft per ticked channel. The name goes in LocationDescription,
      // as single adds do, so inline renames in the table still apply.
      // Each differential picked between two channels of this device is
      // owned by the lower channel's row, which lists the higher channel's
      // new index; pairs with earlier drafts are owned by the new row.
      const rows = batch.selected;
      const pairs = diffsShown ? batch.differentialPairs : [];
      setLocations((prev) => {
        const newLocs: LocationFormData[] = rows.map((r) => ({
          ...locationForm,
          ChannelNo: r.no,
          LocationID: '',
          LocationDescription: r.finalName,
          // Each channel has its own Function; a Temperature Only channel
          // has no concrete start, and one reading the air no limit.
          CalculateMaturity: r.maturity,
          IsAmbient: r.fn === 'ambient',
          ApplyTemperatureLimits: r.fn === 'ambient' ? false : locationForm.ApplyTemperatureLimits,
          EAStart: r.maturity ? locationForm.EAStart : '',
          DiffLocationIndices: diffsShown
            ? batch
                .pourPartnersFor(r.no)
                .map(Number)
                .filter((i) => Number.isInteger(i) && i >= 0 && i < prev.length)
            : [],
        }));
        pairs.forEach((p) => {
          const owner = rows.findIndex((r) => r.no === p.a);
          const partner = rows.findIndex((r) => r.no === p.b);
          if (owner < 0 || partner < 0) return;
          const partnerIdx = prev.length + partner;
          if (newLocs[owner].DiffLocationIndices.includes(partnerIdx)) return;
          newLocs[owner] = {
            ...newLocs[owner],
            DiffLocationIndices: [...newLocs[owner].DiffLocationIndices, partnerIdx],
          };
        });
        return [...prev, ...newLocs];
      });
      added = rows.length;
    } else if (enviroExpandChannels.length > 0) {
      // ConXenviro: silently expand the single entry into one location per
      // real sensor channel (probe + ambient temp + humidity; empty ports
      // skipped) — the same names the dialog previews.
      const newLocs = enviroExpandNames(locationForm.LocationDescription.trim()).map(
        ({ no, name }) => ({
          ...locationForm,
          ChannelNo: no,
          LocationDescription: name,
          // An ambient device never owns a pair; concrete rows pair with it.
          DiffLocationIndices: [],
          // Humidity channels never carry maturity/temp-limits/evaporation or
          // temp-diff pairings (derived MonitorType stays '2').
          ...(enviroExpandChannels.find((c) => c.no === no)?.type === 'HUMIDITY'
            ? {
                CalculateMaturity: false,
                ApplyTemperatureLimits: false,
                CalculateEvaporation: false,
                DiffLocationIndices: [],
              }
            : {}),
        }),
      );
      setLocations((prev) => [...prev, ...newLocs]);
      added = newLocs.length;
    } else {
      // Pairs ticked for an earlier device of another type stay out when
      // this type has no differentials.
      setLocations((prev) => [
        ...prev,
        { ...locationForm, DiffLocationIndices: diffsShown ? locationForm.DiffLocationIndices : [] },
      ]);
      added = 1;
    }

    if (addNext && !isEdit) {
      // Keep the dialog, times and settings; clear the device so the next
      // one can be picked. Pairs between channels stay remembered.
      const device = loggerDisplayName(dialogLogger) || locationForm.LoggerID;
      setAddNotice(`Added ${added} location${added === 1 ? '' : 's'} for ${device}`);
      setAddedCount((n) => n + added);
      setLocationForm((prev) => ({
        ...prev,
        LoggerID: '',
        ChannelNo: 1,
        LocationID: '',
        LocationDescription: '',
        TankID: '',
        // Partners picked for the last device don't apply to the next one.
        DiffLocationIndices: [],
      }));
      batch.resetForNextDevice();
      if (dialogContentRef.current) dialogContentRef.current.scrollTop = 0;
      // Desktop only: focusing on a phone pops the keyboard over the notice.
      if (!isMobile) window.setTimeout(() => deviceInputRef.current?.focus(), 0);
      return;
    }
    setLocationDialogOpen(false);
  };

  /** Removes a draft row. Other rows' differential pairs drop the row and
   *  shift down to keep pointing at the same locations. */
  const deleteLocation = (idx: number) => {
    setLocations((prev) => removeRowAndRenumber(prev, idx));
  };

  // ─── Contact helpers ───
  const addContact = () => {
    if (!newContactId) return;
    if (contacts.some((c) => c.contactId === newContactId)) return;
    setContacts((prev) => [...prev, { contactId: newContactId, sendEmail: true, alert: true }]);
    setNewContactId('');
    setContactDialogOpen(false);
  };

  const removeContact = (idx: number) => {
    setContacts((prev) => prev.filter((_, i) => i !== idx));
  };

  const toggleContactField = (idx: number, field: 'sendEmail' | 'alert') => {
    setContacts((prev) =>
      prev.map((c, i) => (i === idx ? { ...c, [field]: !c[field] } : c)),
    );
  };

  const contactName = (id: string): string =>
    lookups?.contacts.find((c) => c.id === id)?.name ?? id;

  // ─── Quick-add Project dialog ───
  const [addProjectOpen, setAddProjectOpen] = useState(false);
  const [newProjectForm, setNewProjectForm] = useState({ name: '', client: '', address: '', timezone: '' });
  const [newProjectLatLon, setNewProjectLatLon] = useState<{ lat?: string; lon?: string }>({});
  const [addProjectSaving, setAddProjectSaving] = useState(false);

  // Existing client names for the autocomplete; loaded when the dialog first opens
  const [clientOptions, setClientOptions] = useState<string[] | null>(null);
  useEffect(() => {
    if (addProjectOpen && clientOptions === null) {
      projectsService.getClients().then(setClientOptions).catch(() => setClientOptions([]));
    }
  }, [addProjectOpen, clientOptions]);

  const resetProjectDialog = () => {
    setNewProjectForm({ name: '', client: '', address: '', timezone: '' });
    setNewProjectLatLon({});
    setAddProjectOpen(false);
  };

  const handleAddProject = async () => {
    if (!newProjectForm.name.trim() || !user?.CompanyID) return;
    setAddProjectSaving(true);
    try {
      const created = await projectsService.createProject({
        project_name: newProjectForm.name.trim(),
        company_id: user.CompanyID,
        client: newProjectForm.client.trim() || undefined,
        address: newProjectForm.address.trim() || undefined,
        timezone: newProjectForm.timezone || undefined,
        latitude: newProjectLatLon.lat || undefined,
        longitude: newProjectLatLon.lon || undefined,
      });
      // The lookups carry each project's timezone (timezone_iana, which the
      // server works out from the project's zone): read them again so the
      // times picked for the new project are in its timezone, not UTC. The
      // entry is added by hand only when they cannot be read.
      const fresh = await poursAPI.getLookups().catch(() => null);
      setLookups((prev) => {
        const next = fresh ?? prev;
        if (!next) return next;
        return next.projects.some((p) => p.id === created.project_id)
          ? next
          : { ...next, projects: [...next.projects, { id: created.project_id, name: created.project_name }] };
      });
      setProjectId(created.project_id);
      resetProjectDialog();
    } catch (err: any) {
      setError(apiErrorText(err, 'Failed to create project'));
    } finally {
      setAddProjectSaving(false);
    }
  };

  // ─── Quick-add Product dialog ───
  const [addProductOpen, setAddProductOpen] = useState(false);
  const [newProductForm, setNewProductForm] = useState({
    code: '', description: '', strength_grade: '', supplier_plant_id: '',
    mass_concrete_default_id: '', evaporation_default_id: '',
  });
  const [addProductSaving, setAddProductSaving] = useState(false);

  const resetProductDialog = () => {
    setNewProductForm({
      code: '', description: '', strength_grade: '', supplier_plant_id: '',
      mass_concrete_default_id: '', evaporation_default_id: '',
    });
    setAddProductOpen(false);
  };

  const handleAddProduct = async () => {
    if (!newProductForm.code.trim() || !user?.CompanyID) return;
    setAddProductSaving(true);
    try {
      const created = await productsService.createProduct({
        code: newProductForm.code.trim(),
        description: newProductForm.description.trim(),
        company_id: user.CompanyID,
        supplier_plant_id: newProductForm.supplier_plant_id || undefined,
        strength_grade: newProductForm.strength_grade ? parseInt(newProductForm.strength_grade) : undefined,
        mass_concrete_default_id: newProductForm.mass_concrete_default_id || undefined,
        evaporation_default_id: newProductForm.evaporation_default_id || undefined,
      });
      const plantName = supplierPlants.find((p) => p.supplier_plant_id === newProductForm.supplier_plant_id)?.name;
      const newItem: ProductListItem = {
        product_id: created.product_id,
        company_id: created.company_id,
        code: created.code,
        description: created.description,
        supplier_plant_name: plantName || created.supplier_plant_name,
        strength_grade: created.strength_grade,
      };
      setAllProducts((prev) => [...prev, newItem]);
      handleProductSelect(newItem);
      resetProductDialog();
    } catch (err: any) {
      setError(apiErrorText(err, 'Failed to create product'));
    } finally {
      setAddProductSaving(false);
    }
  };

  // ─── Quick-add Batch Plant dialog ───
  const [addPlantOpen, setAddPlantOpen] = useState(false);
  const [newPlantForm, setNewPlantForm] = useState({
    name: '', ready_mix_supplier: '', address: '', latitude: '', longitude: '',
  });
  const [addPlantSaving, setAddPlantSaving] = useState(false);

  const resetPlantDialog = () => {
    setNewPlantForm({ name: '', ready_mix_supplier: '', address: '', latitude: '', longitude: '' });
    setAddPlantOpen(false);
  };

  const handleAddPlant = async () => {
    if (!newPlantForm.name.trim() || !user?.CompanyID) return;
    setAddPlantSaving(true);
    try {
      const created = await productsService.createSupplierPlant({
        name: newPlantForm.name.trim(),
        company_id: user.CompanyID,
        ready_mix_supplier: newPlantForm.ready_mix_supplier.trim() || undefined,
        address: newPlantForm.address.trim() || undefined,
        latitude: newPlantForm.latitude || undefined,
        longitude: newPlantForm.longitude || undefined,
      });
      setSupplierPlants((prev) => [...prev, created].sort((a, b) => a.name.localeCompare(b.name)));
      setSupplierPlantId(created.supplier_plant_id);
      resetPlantDialog();
    } catch (err: any) {
      setError(apiErrorText(err, 'Failed to create batch plant'));
    } finally {
      setAddPlantSaving(false);
    }
  };

  // ─── Loading ───
  if (lookupLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight={400}>
        <CircularProgress />
      </Box>
    );
  }

  // ─── Mix design page (new tab) and reload ───
  // The mix design's maturity inputs, calibration and targets are set on its
  // own page. It opens in a new tab so the wizard is kept; once changed
  // there, Reload mix design reads it again here.
  const mixDesignActions =
    selectedProduct && canViewMixDesign ? (
      <Box sx={{ mt: 1.5 }} data-testid="mix-design-actions">
        <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 1 }}>
          <Button
            component="a"
            href={`/products/${encodeURIComponent(selectedProduct.product_id)}`}
            target="_blank"
            rel="noopener noreferrer"
            size="small"
            variant="outlined"
            endIcon={<OpenInNewIcon fontSize="small" />}
            sx={{ textTransform: 'none' }}
          >
            {t('pourCreate.openMixDesign', 'Open mix design in a new tab')}
          </Button>
          <Button
            size="small"
            variant="text"
            onClick={reloadProduct}
            disabled={reloadingProduct}
            startIcon={reloadingProduct ? <CircularProgress size={14} color="inherit" /> : <RefreshIcon />}
            sx={{ textTransform: 'none' }}
          >
            {t('pourCreate.reloadMixDesign', 'Reload mix design')}
          </Button>
        </Box>
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
          {t(
            'pourCreate.mixDesignHint',
            'Change its maturity inputs, calibration or targets there, then reload it here.',
          )}
        </Typography>
      </Box>
    ) : null;

  // ─── Render Steps ───
  const renderStep0 = () => (
    <Box>
      <Typography variant="h6" sx={{ mb: 1 }}>
        What Type of Pour will you be monitoring?
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Select one or multiple functions. Dependencies are selected automatically.
      </Typography>
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 2 }}>
        {FUNCTION_OPTIONS.map((func) => {
          const allowed = isFunctionAllowed(func.id);
          const selected = selectedFunctions.includes(func.id);
          const autoIncluded = isAutoIncluded(func.id);
          const card = (
            <Paper
              onClick={() => toggleFunction(func.id)}
              sx={{
                p: 2,
                cursor: allowed ? 'pointer' : 'not-allowed',
                border: 2,
                borderColor: selected ? BRAND_GREEN : 'grey.300',
                backgroundColor: selected
                  ? 'rgba(121,168,42,0.14)'
                  : allowed
                    ? 'background.paper'
                    : 'action.disabledBackground',
                boxShadow: selected ? '0 0 0 3px rgba(121,168,42,0.4)' : 'none',
                opacity: allowed ? 1 : 0.55,
                transition: 'all 0.2s',
                '&:hover': allowed ? { borderColor: 'primary.main', boxShadow: 2 } : undefined,
                display: 'flex',
                alignItems: 'center',
                gap: 2,
                position: 'relative',
              }}
            >
              {selected && !autoIncluded && (
                <CheckCircleIcon
                  sx={{
                    position: 'absolute',
                    top: 8,
                    right: 8,
                    fontSize: 26,
                    color: BRAND_GREEN,
                    backgroundColor: 'background.paper',
                    borderRadius: '50%',
                  }}
                />
              )}
              {autoIncluded && (
                <Chip
                  label="Required"
                  size="small"
                  sx={{
                    position: 'absolute',
                    top: 8,
                    right: 8,
                    backgroundColor: BRAND_GREEN,
                    color: 'primary.contrastText',
                    fontSize: '0.75rem',
                    height: 20,
                  }}
                />
              )}
              {!allowed && (
                <Chip
                  icon={<LockIcon sx={{ fontSize: 14 }} />}
                  label={t('pourFunction.notInPlan', 'Not included in your plan')}
                  size="small"
                  sx={{
                    position: 'absolute',
                    top: 8,
                    right: 8,
                    backgroundColor: 'grey.300',
                    color: 'text.secondary',
                    fontSize: '0.75rem',
                    height: 20,
                  }}
                />
              )}
              <Box
                component="img"
                src={func.img}
                alt=""
                sx={{
                  width: 88,
                  height: 72,
                  objectFit: 'contain',
                  flexShrink: 0,
                  filter: allowed ? 'none' : 'grayscale(1)',
                }}
              />
              <Box>
                <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 0.5 }}>
                  {t(func.titleKey, func.titleFallback)}
                </Typography>
                <Typography variant="body2" sx={{ opacity: 0.85, fontSize: '0.8rem' }}>
                  {t(func.descKey, func.descFallback)}
                </Typography>
              </Box>
            </Paper>
          );
          if (allowed) return <React.Fragment key={func.id}>{card}</React.Fragment>;
          return (
            <Tooltip
              key={func.id}
              title={t(
                'pourFunction.notInPlanTooltip',
                'This feature is not included in your current subscription. Please contact ConXedge if you would like to enable it.',
              )}
              arrow
            >
              {card}
            </Tooltip>
          );
        })}
      </Box>
    </Box>
  );

  const renderStep1 = () => (
    <Box sx={{ fontSize: '0.875rem' }}>
      <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2.5 }}>
        Pour Details
      </Typography>

      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: { xs: 3, md: 4 } }}>
        {/* ── Left column: Form fields ── */}
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <TextField
            label="Pour Name"
            size="small"
            value={pourName}
            onChange={(e) => {
              setPourName(e.target.value);
              if (fieldErrors.pourName) setFieldErrors((p) => ({ ...p, pourName: '' }));
            }}
            fullWidth
            required
            inputProps={{ maxLength: 50 }}
            error={!!fieldErrors.pourName}
            helperText={fieldErrors.pourName}
          />

          {/* Pour Start / Finish Time */}
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2 }}>
            <DateTimePicker
              label={tzLabelled('Pour Start Time', pourStartTime)}
              value={pourStartTime ? dayjs(pourStartTime) : null}
              onChange={(val: Dayjs | null) => {
                setPourStartEdited(true);
                setPourStartTime(val ? val.format('YYYY-MM-DDTHH:mm') : '');
              }}
              minutesStep={30}
              ampm={false}
              slotProps={{
                textField: { fullWidth: true, size: 'small' },
                actionBar: { sx: { display: 'none' } },
                popper: { sx: PICKER_POPPER_SX },
              }}
            />
            <DateTimePicker
              label={tzLabelled('Pour Finish Time', pourFinishTime)}
              value={pourFinishTime ? dayjs(pourFinishTime) : null}
              onChange={(val: Dayjs | null) => setPourFinishTime(val ? val.format('YYYY-MM-DDTHH:mm') : '')}
              minutesStep={30}
              ampm={false}
              slotProps={{
                textField: { fullWidth: true, size: 'small' },
                actionBar: { sx: { display: 'none' } },
                popper: { sx: PICKER_POPPER_SX },
              }}
            />
          </Box>

          <FormControlLabel
            control={
              <Checkbox
                checked={startAllSensors}
                onChange={(e) => setStartAllSensors(e.target.checked)}
                sx={{ color: BRAND_GREEN, '&.Mui-checked': { color: BRAND_GREEN } }}
              />
            }
            label={
              <Typography variant="body2">
                Start all the temperature sensors at the &ldquo;Pour Start Time&rdquo;
              </Typography>
            }
            sx={{ mt: -1 }}
          />

          {/* Stop Monitoring: after a number of days */}
          <StopMonitoringFields
            stopType={stopType}
            xHours={xHours}
            errors={{ xHours: fieldErrors.xHours }}
            onChange={(next) => {
              setStopType(next.stopType);
              setXHours(next.xHours);
              setFieldErrors((p) => ({ ...p, stopType: '', xHours: '' }));
            }}
          />

          {/* Project */}
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}>
            <Autocomplete
              options={lookups?.projects || []}
              getOptionLabel={(p) => p.name || ''}
              value={lookups?.projects.find((p) => p.id === projectId) || null}
              onChange={(_e, newValue) => {
                setProjectId(newValue?.id || '');
                if (fieldErrors.projectId) setFieldErrors((p) => ({ ...p, projectId: '' }));
              }}
              isOptionEqualToValue={(option, value) => option.id === value.id}
              noOptionsText="No projects available"
              size="small"
              slotProps={DROPDOWN_AUTOCOMPLETE_SLOT_PROPS}
              sx={{ flex: 1 }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Project"
                  fullWidth
                  required
                  error={!!fieldErrors.projectId}
                  helperText={fieldErrors.projectId}
                />
              )}
            />
            {canAddProject && (
              <IconButton sx={{ color: BRAND_GREEN }} title="Add new project" onClick={() => setAddProjectOpen(true)}>
                <AddCircleIcon />
              </IconButton>
            )}
          </Box>

          {/* Product / Recipe (conditional) */}
          {isFieldVisible('productCode') && (
            <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}>
              <Autocomplete
                options={productOptions}
                getOptionLabel={formatProductOption}
                value={selectedProduct}
                onChange={(_e, newValue) => {
                  handleProductSelect(newValue);
                  if (fieldErrors.product) setFieldErrors((p) => ({ ...p, product: '' }));
                }}
                isOptionEqualToValue={(option, value) => option.product_id === value.product_id}
                noOptionsText="No products available"
                size="small"
                slotProps={DROPDOWN_AUTOCOMPLETE_SLOT_PROPS}
                sx={{ flex: 1 }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Product / Recipe"
                    fullWidth
                    required
                    error={!!fieldErrors.product}
                    helperText={fieldErrors.product}
                  />
                )}
              />
              {canAddProduct && (
                <IconButton sx={{ color: BRAND_GREEN }} title="Add new product" onClick={() => setAddProductOpen(true)}>
                  <AddCircleIcon />
                </IconButton>
              )}
            </Box>
          )}

          {/* Batch plant (conditional) */}
          {isFieldVisible('supplierPlant') && (
            <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}>
              <Autocomplete
                options={supplierPlants}
                getOptionLabel={(p) => p.name}
                value={supplierPlants.find((p) => p.supplier_plant_id === supplierPlantId) || null}
                onChange={(_e, newValue) => setSupplierPlantId(newValue?.supplier_plant_id || '')}
                isOptionEqualToValue={(option, value) => option.supplier_plant_id === value.supplier_plant_id}
                noOptionsText="No batch plants available"
                size="small"
                slotProps={DROPDOWN_AUTOCOMPLETE_SLOT_PROPS}
                sx={{ flex: 1 }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Supplier / Batch Plant"
                    fullWidth
                    helperText={
                      supplierPlantId && selectedProduct
                        ? 'The mix design will be linked to this plant when the pour is saved.'
                        : undefined
                    }
                  />
                )}
              />
              {canAddPlant && (
                <IconButton sx={{ color: BRAND_GREEN }} title="Add new batch plant" onClick={() => setAddPlantOpen(true)}>
                  <AddCircleIcon />
                </IconButton>
              )}
            </Box>
          )}

          {/* Limit groups (conditional) */}
          {isFieldVisible('maturityTargetGroup') && (
            <FormControl fullWidth size="small">
              <InputLabel>Maturity Target Group</InputLabel>
              <Select
                value={targetGroupId}
                label="Maturity Target Group"
                onChange={(e: SelectChangeEvent) => setTargetGroupId(e.target.value)}
                MenuProps={SELECT_MENU_PROPS}
              >
                <MenuItem value=""><em>None</em></MenuItem>
                {productTargetGroups.map((tg) => (
                  <MenuItem key={tg.target_group_id} value={tg.target_group_id}>
                    {tg.target_group_name || tg.target_group_id}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}
          {isFieldVisible('massConcreteLimitGroup') && (
            <FormControl fullWidth size="small">
              <InputLabel>Mass Concrete Limit Group</InputLabel>
              <Select
                value={limitGroupId}
                label="Mass Concrete Limit Group"
                onChange={(e: SelectChangeEvent) => setLimitGroupId(e.target.value)}
                MenuProps={SELECT_MENU_PROPS}
              >
                <MenuItem value=""><em>None</em></MenuItem>
                {(lookups?.mass_concrete_defaults || []).map((mc) => (
                  <MenuItem key={mc.id} value={mc.id}>{mc.name}</MenuItem>
                ))}
              </Select>
            </FormControl>
          )}
          {isFieldVisible('evaporationLimitGroup') && (
            <FormControl fullWidth size="small">
              <InputLabel>Evaporation Limit Group</InputLabel>
              <Select
                value={evaLimitGroupId}
                label="Evaporation Limit Group"
                onChange={(e: SelectChangeEvent) => setEvaLimitGroupId(e.target.value)}
                MenuProps={SELECT_MENU_PROPS}
              >
                <MenuItem value=""><em>None</em></MenuItem>
                {(lookups?.evaporation_defaults || []).map((ed) => (
                  <MenuItem key={ed.id} value={ed.id}>{ed.name}</MenuItem>
                ))}
              </Select>
            </FormControl>
          )}

          <TextField
            label="Drawing / Reference Number"
            size="small"
            value={projectRef}
            onChange={(e) => setProjectRef(e.target.value)}
            fullWidth
          />
          <TextField
            label="Additional Notes / Comments"
            size="small"
            value={levels}
            onChange={(e) => setLevels(e.target.value)}
            fullWidth
            multiline
            rows={3}
          />
        </Box>

        {/* ── Right column: Attachments + Additional Info ── */}
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
          <input
            type="file"
            ref={fileInputRef}
            onChange={(e) => {
              const files = e.target.files;
              if (files) setStagedFiles((prev) => [...prev, ...Array.from(files)]);
              e.target.value = '';
            }}
            accept="image/jpeg,image/png,image/gif,image/webp,application/pdf,.doc,.docx,.xls,.xlsx"
            multiple
            style={{ display: 'none' }}
          />
          <Paper
            onClick={() => fileInputRef.current?.click()}
            elevation={0}
            sx={{
              p: 4,
              border: '2px dashed',
              borderColor: stagedFiles.length > 0 ? BRAND_GREEN : 'grey.300',
              backgroundColor: 'grey.50',
              textAlign: 'center',
              minHeight: 160,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              cursor: 'pointer',
              borderRadius: 2,
              transition: 'border-color 0.2s',
              '&:hover': { borderColor: BRAND_GREEN },
            }}
          >
            <AddIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 1 }} />
            <Typography variant="body2" color="text.secondary">
              Click to add attachments (images, PDF, Word, Excel)
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Max 25MB per file
            </Typography>
          </Paper>

          {stagedFiles.length > 0 && (
            <Paper variant="outlined" sx={{ borderRadius: 2 }}>
              {stagedFiles.map((file, idx) => (
                <Box
                  key={idx}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    px: 2,
                    py: 1,
                    borderBottom: idx < stagedFiles.length - 1 ? '1px solid' : 'none',
                    borderColor: 'divider',
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, overflow: 'hidden' }}>
                    {stagedPreviews[idx] && (
                      <Box
                        component="img"
                        src={stagedPreviews[idx] as string}
                        alt={file.name}
                        onClick={(e: React.MouseEvent) => {
                          e.stopPropagation();
                          setImagePreview({ src: stagedPreviews[idx] as string, name: file.name });
                        }}
                        sx={{
                          width: 64,
                          height: 64,
                          objectFit: 'cover',
                          borderRadius: 1,
                          flexShrink: 0,
                          border: '1px solid',
                          borderColor: 'divider',
                          cursor: 'pointer',
                          '&:hover': { boxShadow: 2 },
                        }}
                      />
                    )}
                    <Box sx={{ overflow: 'hidden' }}>
                      <Typography variant="body2" noWrap>{file.name}</Typography>
                      <Typography variant="caption" color="text.secondary">
                        {(file.size / 1024).toFixed(0)} KB
                      </Typography>
                    </Box>
                  </Box>
                  <IconButton
                    size="small"
                    color="error"
                    onClick={(e) => {
                      e.stopPropagation();
                      setStagedFiles((prev) => prev.filter((_, i) => i !== idx));
                    }}
                  >
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </Box>
              ))}
            </Paper>
          )}

          {/* Additional Information */}
          <Box>
            <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1.5 }}>
              Additional Information
            </Typography>
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '110px 1fr', sm: '160px 1fr' }, columnGap: 1.5, rowGap: 0.75, fontSize: '0.875rem' }}>
              <Typography variant="body2" color="text.secondary">Date Created</Typography>
              <Typography variant="body2">{new Date().toLocaleDateString()}</Typography>
              {selectedProduct && (
                <>
                  <Typography variant="body2" color="text.secondary">Product Code</Typography>
                  <Typography variant="body2">{selectedProduct.code}</Typography>
                  <Typography variant="body2" color="text.secondary">Description</Typography>
                  <Typography variant="body2">{selectedProduct.description || '-'}</Typography>
                  {selectedProduct.supplier_plant_name && (
                    <>
                      <Typography variant="body2" color="text.secondary">Supplier Plant</Typography>
                      <Typography variant="body2">{selectedProduct.supplier_plant_name}</Typography>
                    </>
                  )}
                  {selectedProduct.strength_grade != null && (
                    <>
                      <Typography variant="body2" color="text.secondary">Strength Grade</Typography>
                      <Typography variant="body2">{valueWithUnit(selectedProduct.strength_grade, productStrengthUnit)}</Typography>
                    </>
                  )}
                  {selectedFunctions.includes('Concrete Maturity') && productDetail?.maturity_method && (
                    <>
                      <Typography variant="body2" color="text.secondary">Maturity Method</Typography>
                      <Typography variant="body2">{productDetail.maturity_method}</Typography>
                    </>
                  )}
                  {selectedFunctions.includes('Concrete Maturity') && productDetail?.maturity_group_id && (
                    <>
                      <Typography variant="body2" color="text.secondary">Maturity Input Group</Typography>
                      <Typography variant="body2">{productDetail.maturity_group_id}</Typography>
                    </>
                  )}
                  {selectedFunctions.includes('Concrete Maturity') && productDetail?.maturity_datum_temp != null && (
                    <>
                      <Typography variant="body2" color="text.secondary">Datum Temperature</Typography>
                      <Typography variant="body2">{productDetail.maturity_datum_temp} °C</Typography>
                    </>
                  )}
                  {selectedFunctions.includes('Concrete Maturity') && productDetail?.maturity_specified_temp != null && (
                    <>
                      <Typography variant="body2" color="text.secondary">Specified Temperature</Typography>
                      <Typography variant="body2">{productDetail.maturity_specified_temp} °C</Typography>
                    </>
                  )}
                  {selectedFunctions.includes('Concrete Maturity') && activationEnergyDisplay != null && (
                    <>
                      <Typography variant="body2" color="text.secondary">Activation Energy</Typography>
                      <Typography variant="body2">
                        {activationEnergyDisplay} J/mol
                        {qDisplay != null ? ` (Q ${qDisplay} K${plannedBasis?.q_is_default ? ', default' : ''})` : ''}
                      </Typography>
                    </>
                  )}
                  {selectedFunctions.includes('Concrete Maturity') && calibrationLabel && (
                    <>
                      <Typography variant="body2" color="text.secondary">Calibration</Typography>
                      <Typography variant="body2">{calibrationLabel}</Typography>
                    </>
                  )}
                </>
              )}
            </Box>
            {showCalibrationCheck && <Box sx={{ mt: 1.5 }}>{calibrationCheckPanel}</Box>}
            {computesMaturity && mixDesignActions}
          </Box>
        </Box>
      </Box>
    </Box>
  );

  const renderStep2 = () => (
    <Box>
      <Typography variant="h6" sx={{ mb: 2 }}>
        Hardware / Sensors
      </Typography>
      {stationNotes.map((note) => (
        <Alert key={note} severity="warning" sx={{ mb: 2 }}>{note}</Alert>
      ))}

      {isMobile ? (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, mb: 2 }}>
          {locations.length === 0 ? (
            <Paper variant="outlined" sx={{ p: 3, textAlign: 'center', borderStyle: 'dashed' }}>
              <Typography variant="body2" color="text.secondary">
                No locations added yet. Tap &ldquo;Create a new location&rdquo; below to add a monitoring location.
              </Typography>
            </Paper>
          ) : (
            locations.map((loc, idx) => {
              const logger = lookups?.loggers.find((l) => l.id === loc.LoggerID);
              const flags: string[] = [];
              if (loc.CalculateMaturity) flags.push('Maturity');
              if (loc.IsAmbient) flags.push('Ambient');
              if (loc.CalculateTMC) flags.push('TMC');
              if (loc.CalculateEvaporation) flags.push('Evaporation');
              if (loc.ApplyTemperatureLimits) flags.push('Temp Limits');
              // The Differentials line below shows the pairs when the pour has them.
              if (!showDiffColumn && symmetricPartners(locations, idx).length > 0) flags.push('Differential');
              return (
                <Card key={idx} elevation={1}>
                  <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 1 }}>
                      <Box sx={{ minWidth: 0 }}>
                        <Typography variant="subtitle2" fontWeight={600} noWrap>
                          {loc.LocationDescription || `Location ${idx + 1}`}
                        </Typography>
                        <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 0.75, mt: 0.5 }}>
                          {loc.HardwareType && <Chip label={loc.HardwareType} size="small" variant="outlined" />}
                          <Typography variant="caption" color="text.secondary">
                            {loggerDisplayName(logger) || loc.LoggerID || 'No device'}
                            {isFieldVisible('channelNumber') && ` · ch.${loc.ChannelNo}`}
                          </Typography>
                        </Box>
                      </Box>
                      <Box sx={{ flexShrink: 0 }}>
                        <IconButton size="small" onClick={() => openEditLocation(idx)} title="Edit">
                          <EditIcon fontSize="small" />
                        </IconButton>
                        <IconButton size="small" color="error" onClick={() => deleteLocation(idx)} title="Delete">
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Box>
                    </Box>
                    <Box sx={{ display: 'grid', gridTemplateColumns: 'auto 1fr', columnGap: 1.5, rowGap: 0.5, mt: 1.5 }}>
                      <Typography variant="caption" color="text.secondary">Start</Typography>
                      <Typography variant="caption">{tableTime(loc.LogginStart)}</Typography>
                      {isFieldVisible('concreteTemperatureStartTime') && (
                        <>
                          <Typography variant="caption" color="text.secondary" title={concreteStartHelp}>
                            {concreteStartLabel}
                          </Typography>
                          <Typography variant="caption">
                            {isSiteHardware(loc.HardwareType) || !loc.CalculateMaturity ? '-' : tableTime(loc.EAStart)}
                          </Typography>
                        </>
                      )}
                      <Typography variant="caption" color="text.secondary">End</Typography>
                      <Typography variant="caption">{tableTime(loc.LoggingEnd)}</Typography>
                      {isFieldVisible('tank') && loc.TankID && (
                        <>
                          <Typography variant="caption" color="text.secondary">Tank</Typography>
                          <Typography variant="caption">{lookups?.tanks.find((tk) => tk.id === loc.TankID)?.name || loc.TankID}</Typography>
                        </>
                      )}
                      {showDiffColumn && (
                        <>
                          <Typography variant="caption" color="text.secondary">Differentials</Typography>
                          {renderDraftDiffs(loc, idx, 'inline')}
                        </>
                      )}
                    </Box>
                    {flags.length > 0 && (
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 1 }}>
                        {flags.map((f) => (
                          <Chip key={f} label={f} size="small" sx={{ height: 20, fontSize: '0.68rem' }} />
                        ))}
                      </Box>
                    )}
                  </CardContent>
                </Card>
              );
            })
          )}
        </Box>
      ) : (
      <TableContainer component={Paper} variant="outlined" sx={{ mb: 2, borderRadius: '12px' }}>
        <Table size="small" sx={{ '& tbody td': { py: 1.25 }, '& td, & th': { px: 1 } }}>
          <TableHead>
            <TableRow sx={{ '& th': { color: 'text.secondary', fontSize: '0.72rem', fontWeight: 600, whiteSpace: 'nowrap' } }}>
              <TableCell sx={TABLE_ICON_CELL_SX} />
              <TableCell>Location Name</TableCell>
              {isFieldVisible('channelNumber') && <TableCell align="center">Ch</TableCell>}
              <TableCell>Function</TableCell>
              <TableCell>Start</TableCell>
              {isFieldVisible('concreteTemperatureStartTime') && (
                <TableCell title={`${concreteStartLabel}. ${concreteStartHelp}`}>{CONCRETE_START_SHORT_LABEL}</TableCell>
              )}
              <TableCell>End</TableCell>
              {showTempLimitColumn && <TableCell align="center" sx={TABLE_TICK_HEAD_SX}>Temp Limit</TableCell>}
              {showEvaporationColumn && <TableCell align="center" sx={TABLE_TICK_HEAD_SX}>Evaporation</TableCell>}
              {isFieldVisible('tank') && <TableCell>Tank</TableCell>}
              {showDiffColumn && <TableCell>Differentials</TableCell>}
              <TableCell sx={TABLE_ICON_CELL_SX} />
            </TableRow>
          </TableHead>
          <TableBody>
            {locations.length === 0 ? (
              <TableRow>
                <TableCell colSpan={locationTableColumns} align="center" sx={{ py: 4 }}>
                  <Typography variant="body2" color="text.secondary">
                    No hardware added yet. Click &ldquo;Add hardware&rdquo; below to add a monitoring location.
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              draftDeviceGroups.map((group) => [
                <TableRow key={`device-${group.key}`} data-testid={`device-group-${group.key}`}>
                  <TableCell colSpan={locationTableColumns} sx={{ bgcolor: 'grey.50', py: '10px !important' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                      <HardwareTypeImage type={group.hw} size={40} />
                      <Box sx={{ minWidth: 0 }}>
                        <Typography variant="body2" sx={{ fontWeight: 700 }}>
                          {HARDWARE_TYPE_NAMES[group.hw]} · {group.deviceName}
                        </Typography>
                        {group.hw === 'ConXagent' && (
                          <Typography variant="caption" color="text.secondary">
                            {group.rows.length} of 4 channels on this pour
                          </Typography>
                        )}
                      </Box>
                    </Box>
                  </TableCell>
                </TableRow>,
                ...group.rows.map(({ loc, idx }) => {
                  const updateLoc = (patch: Partial<LocationFormData>) =>
                    setLocations((prev) => prev.map((l, i) => (i === idx ? { ...l, ...patch } : l)));
                  // Channels other rows already use on this row's device.
                  const usedByOthers = locations
                    .filter((other, i) => i !== idx && other.LoggerID === loc.LoggerID)
                    .map((other) => Number(other.ChannelNo));
                  const concrete = isConcreteDraft(loc);
                  const hwFields = HARDWARE_TYPE_FIELDS[loc.HardwareType];
                  const fixedFunction = locationFunctionView(loc.HardwareType, computesMaturity);
                  return (
                    <TableRow key={idx}>
                      <TableCell sx={TABLE_ICON_CELL_SX}>
                        <IconButton size="small" onClick={() => openEditLocation(idx)} title="Edit full details">
                          <EditIcon fontSize="small" />
                        </IconButton>
                      </TableCell>
                      <TableCell sx={TABLE_NAME_CELL_SX}>
                        <TextField
                          value={loc.LocationDescription}
                          onChange={(e) => updateLoc({ LocationDescription: e.target.value })}
                          // Enter finishes the name; it never adds a line.
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') {
                              e.preventDefault();
                              (e.target as HTMLInputElement).blur();
                            }
                          }}
                          size="small"
                          variant="standard"
                          placeholder="Location name"
                          fullWidth
                          multiline
                          maxRows={3}
                          inputProps={{ maxLength: LOCATION_NAME_MAX, style: { fontSize: '0.85rem', fontWeight: 600 } }}
                        />
                      </TableCell>
                      {isFieldVisible('channelNumber') && (
                        <TableCell align="center">
                          {hwFields?.channelNumber === false ? (
                            NOT_APPLICABLE
                          ) : isSingleChannelHardware(loc.HardwareType) ? (
                            loc.ChannelNo
                          ) : (
                            <TextField
                              value={loc.ChannelNo}
                              onChange={(e) => updateLoc({ ChannelNo: Number(e.target.value) })}
                              select
                              size="small"
                              variant="standard"
                              sx={{ width: 40 }}
                              SelectProps={{ style: { fontSize: '0.85rem' } }}
                            >
                              {CHANNEL_NUMBERS.map((ch) => (
                                <MenuItem key={ch} value={ch} disabled={usedByOthers.includes(ch)}>{ch}</MenuItem>
                              ))}
                            </TextField>
                          )}
                        </TableCell>
                      )}
                      <TableCell>
                        {fixedFunction.kind === 'fixed' ? (
                          <Typography variant="body2" sx={{ fontSize: '0.8rem' }}>
                            {fixedFunction.label}
                          </Typography>
                        ) : (
                          <LocationFunctionToggle
                            value={loc.CalculateMaturity ? 'maturity' : 'temp'}
                            onChange={(fn) =>
                              updateLoc({
                                CalculateMaturity: fn === 'maturity',
                                IsAmbient: false,
                                EAStart: fn === 'maturity' ? loc.EAStart || loc.LogginStart : '',
                              })
                            }
                            maturityAllowed={computesMaturity}
                            maturityName={dialogMaturityName}
                            ariaLabel={`${loc.LocationDescription || `Location ${idx + 1}`} function`}
                          />
                        )}
                      </TableCell>
                      <TableCell
                        sx={{ ...TABLE_TIME_CELL_SX, cursor: 'pointer', '&:hover': { color: 'primary.main' } }}
                        onClick={(e) => setDatePopover({ anchorEl: e.currentTarget, locIdx: idx, field: 'LogginStart' })}
                      >
                        {tableTime(loc.LogginStart)}
                      </TableCell>
                      {isFieldVisible('concreteTemperatureStartTime') && (
                        isSiteHardware(loc.HardwareType) || !loc.CalculateMaturity ? (
                          // Site hardware and Temperature locations have no
                          // concrete start.
                          <TableCell sx={TABLE_TIME_CELL_SX}>{NOT_APPLICABLE}</TableCell>
                        ) : (
                          <TableCell
                            sx={{ ...TABLE_TIME_CELL_SX, cursor: 'pointer', '&:hover': { color: 'primary.main' } }}
                            onClick={(e) => setDatePopover({ anchorEl: e.currentTarget, locIdx: idx, field: 'EAStart' })}
                          >
                            {tableTime(loc.EAStart)}
                          </TableCell>
                        )
                      )}
                      <TableCell
                        sx={{ ...TABLE_TIME_CELL_SX, cursor: 'pointer', '&:hover': { color: 'primary.main' } }}
                        onClick={(e) => setDatePopover({ anchorEl: e.currentTarget, locIdx: idx, field: 'LoggingEnd' })}
                      >
                        {tableTime(loc.LoggingEnd)}
                      </TableCell>
                      {showTempLimitColumn && (
                        <TableCell align="center">
                          {concrete && hwFields?.temperatureLimits !== false ? (
                            <Checkbox
                              size="small"
                              checked={loc.ApplyTemperatureLimits}
                              onChange={(e) => updateLoc({ ApplyTemperatureLimits: e.target.checked })}
                              inputProps={{ 'aria-label': `Temperature limit for ${loc.LocationDescription || `Location ${idx + 1}`}` }}
                              sx={{ p: 0.5, '&.Mui-checked': { color: BRAND_GREEN } }}
                            />
                          ) : (
                            NOT_APPLICABLE
                          )}
                        </TableCell>
                      )}
                      {showEvaporationColumn && (
                        <TableCell align="center">
                          {concrete ? (
                            <Checkbox
                              size="small"
                              checked={loc.CalculateEvaporation}
                              // Saved as hasWeather (either flag), so both follow the tick.
                              onChange={(e) => updateLoc({ CalculateEvaporation: e.target.checked, hasWeather: e.target.checked })}
                              inputProps={{ 'aria-label': `Evaporation rate for ${loc.LocationDescription || `Location ${idx + 1}`}` }}
                              sx={{ p: 0.5, '&.Mui-checked': { color: BRAND_GREEN } }}
                            />
                          ) : (
                            NOT_APPLICABLE
                          )}
                        </TableCell>
                      )}
                      {isFieldVisible('tank') && (
                        <TableCell>
                          {loc.HardwareType === 'TMC' ? (
                            <TextField
                              value={loc.TankID}
                              onChange={(e) => updateLoc({ TankID: e.target.value })}
                              select
                              size="small"
                              variant="standard"
                              fullWidth
                              SelectProps={{ style: { fontSize: '0.85rem' } }}
                            >
                              {availableTanks(loc.TankID).map((t) => (
                                <MenuItem key={t.id} value={t.id}>{t.name}</MenuItem>
                              ))}
                            </TextField>
                          ) : (
                            NOT_APPLICABLE
                          )}
                        </TableCell>
                      )}
                      {showDiffColumn && (
                        <TableCell sx={{ minWidth: 120, maxWidth: 190 }}>
                          {renderDraftDiffs(loc, idx, 'cell')}
                        </TableCell>
                      )}
                      <TableCell sx={TABLE_ICON_CELL_SX}>
                        <IconButton size="small" color="error" onClick={() => deleteLocation(idx)} title="Remove location">
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  );
                }),
              ])
            )}
          </TableBody>
        </Table>
      </TableContainer>
      )}

      {/* Inline date picker — opens calendar directly on cell click */}
      {datePopover && (
        <DateTimePicker
          open
          onClose={() => setDatePopover(null)}
          value={
            locations[datePopover.locIdx]?.[datePopover.field]
              ? dayjs(locations[datePopover.locIdx][datePopover.field])
              : null
          }
          onChange={(val: Dayjs | null) => {
            const { locIdx, field } = datePopover;
            setLocations((prev) =>
              prev.map((l, i) =>
                i === locIdx ? { ...l, [field]: val ? val.format('YYYY-MM-DDTHH:mm') : '' } : l
              )
            );
          }}
          minutesStep={5}
          ampm={false}
          slotProps={{
            textField: {
              sx: { position: 'absolute', width: 0, height: 0, overflow: 'hidden', opacity: 0, pointerEvents: 'none' },
            },
            actionBar: { sx: { display: 'none' } },
            popper: { anchorEl: datePopover.anchorEl, sx: PICKER_POPPER_SX },
          }}
        />
      )}

      <Button
        variant="contained"
        startIcon={<AddIcon />}
        onClick={openAddLocation}
        fullWidth={isMobile}
        sx={{ textTransform: 'none', ...(isMobile && { py: 1.25 }) }}
      >
        Add hardware
      </Button>

      {/* Location dialog: where a sensor is (hardware, channel, name,
          function, times). Limits, differentials and evaporation are
          switched on in the table. */}
      <Dialog
        open={locationDialogOpen}
        onClose={() => setLocationDialogOpen(false)}
        maxWidth={false}
        fullScreen={isMobile}
        PaperProps={{
          'aria-label': editingLocationIdx !== null ? 'Edit hardware' : 'Add hardware',
          sx: isMobile
            ? { display: 'flex', flexDirection: 'column' }
            : {
                width: 560,
                maxWidth: 'calc(100% - 32px)',
                height: 'min(880px, calc(100% - 64px))',
                borderRadius: '12px',
                display: 'flex',
                flexDirection: 'column',
              },
        }}
      >
        <Box sx={{ px: 3, pt: 2.5, pb: 1.5, borderBottom: '1px solid', borderColor: 'grey.200', display: 'flex', alignItems: 'flex-start', gap: 1 }}>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography variant="caption" color="text.secondary">
              Hardware &amp; sensors
            </Typography>
            <Typography variant="h6" sx={{ fontWeight: 700, fontSize: '1.1rem', lineHeight: 1.3 }}>
              {editingLocationIdx !== null
                ? `Edit ${locations[editingLocationIdx]?.LocationDescription || 'location'}`
                : 'Add hardware'}
            </Typography>
          </Box>
          <IconButton onClick={() => setLocationDialogOpen(false)} edge="end" aria-label="Close">
            <CloseIcon />
          </IconButton>
        </Box>
        <Box ref={dialogContentRef} sx={{ flex: 1, overflowY: 'auto', px: 3, py: 2 }}>
          {addNotice && (
            <Alert severity="success" sx={{ mb: 1.5, py: 0 }}>
              {addNotice}
            </Alert>
          )}
          {shownLocationErrors.length > 0 && (
            <Alert severity="error" sx={{ mb: 1.5, py: 0 }}>
              {shownLocationErrors.map((e, i) => (
                <div key={i}>{e}</div>
              ))}
            </Alert>
          )}

          {/* Only the types the selected monitoring functions support. */}
          <DialogStep n={1} title="Hardware type">
            <HardwareTypeCards
              options={dialogHardwareOptions}
              value={locationForm.HardwareType}
              onChange={(value) => changeDialogHardwareType(value as HardwareType)}
            />
          </DialogStep>

          {/* Hardware Number: type to search ("734"), Enter picks it */}
          <DialogStep n={2} title="Hardware number">
            <HardwareNumberPicker
              options={dialogDeviceOptions}
              value={locationForm.LoggerID}
              onChange={handleDialogDeviceChange}
              required
              disabled={!locationForm.HardwareType}
              inputRef={deviceInputRef}
            />
          </DialogStep>

          <DialogStep n={3} title={multiMode ? 'Channels & locations' : 'Location'}>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
              {/* Every channel of a ConXagent at once, a name and a Function
                  each */}
              {multiMode && (
                <ChannelRowsPicker
                  {...batch.pickerProps}
                  pourDiffOptions={diffPartners.map(({ idx }) => ({ id: String(idx), label: draftDiffLabel(idx) }))}
                  onEnter={() => saveLocation(true)}
                  disabled={saving}
                  firstNameInputRef={firstNameInputRef}
                  functionOptions={dialogFunctionView.kind === 'choice' ? dialogFunctionView.options : undefined}
                  maturityName={dialogMaturityName}
                />
              )}

              {/* Hardware Channel Number (not for single-channel devices) */}
              {isLocFieldVisible('channelNumber') && !multiMode && !isSingleChannelHardware(locationForm.HardwareType) && (
                <Box>
                  <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
                    Hardware Channel Number
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 0.5, mt: 0.5 }}>
                    {CHANNEL_NUMBERS.map((ch) => {
                      const active = locationForm.ChannelNo === ch;
                      const usedByOther = locationForm.LoggerID && locations.some(
                        (loc, i) => loc.LoggerID === locationForm.LoggerID && loc.ChannelNo === ch && i !== editingLocationIdx
                      );
                      return (
                        <Box
                          key={ch}
                          onClick={() => { if (!usedByOther) setLocationForm({ ...locationForm, ChannelNo: ch }); }}
                          sx={{
                            width: 32,
                            height: 32,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            border: '1px solid',
                            borderColor: usedByOther ? 'grey.200' : active ? BRAND_GREEN : 'grey.300',
                            borderRadius: '6px',
                            backgroundColor: usedByOther ? 'grey.100' : active ? BRAND_GREEN : 'transparent',
                            color: usedByOther ? 'grey.400' : active ? '#1a1a2e' : 'text.primary',
                            fontWeight: 600,
                            fontSize: '0.85rem',
                            cursor: usedByOther ? 'not-allowed' : 'pointer',
                          }}
                        >
                          {ch}
                        </Box>
                      );
                    })}
                  </Box>
                </Box>
              )}

              {/* Location Name */}
              {!multiMode && (
                <TextField
                  label="Location Name"
                  size="small"
                  value={locationForm.LocationDescription}
                  onChange={(e) => setLocationForm({ ...locationForm, LocationDescription: e.target.value })}
                  fullWidth
                  required
                  inputProps={{ maxLength: LOCATION_NAME_MAX }}
                  helperText={
                    locationForm.HardwareType === 'Temperature & Humidity'
                      ? undefined
                      : 'e.g. Middle, Bottom, Surface, Ambient'
                  }
                  FormHelperTextProps={{ sx: { ml: 0, mt: 0.25, fontSize: '0.7rem' } }}
                />
              )}

              {/* Preview of the locations a T&H sensor will create — shown from
                  the moment the dialog opens (generic pattern), refined to the
                  exact channel list once a device is selected, live-updated as
                  the user types the name. */}
              {editingLocationIdx === null &&
                locationForm.HardwareType === 'Temperature & Humidity' &&
                (enviroExpandChannels.length > 0 || !locationForm.LoggerID) && (
                <Box
                  sx={{
                    backgroundColor: 'grey.50',
                    border: '1px solid',
                    borderColor: 'grey.200',
                    borderRadius: 1,
                    px: 1.5,
                    py: 1,
                  }}
                >
                  <Typography variant="caption" sx={{ fontWeight: 600, color: 'text.secondary' }}>
                    {enviroExpandChannels.length > 0
                      ? `This sensor records ${enviroExpandChannels.length} readings, saving creates:`
                      : 'One location is created per sensor reading, for example:'}
                  </Typography>
                  {(enviroExpandChannels.length > 0
                    ? enviroExpandNames(
                        locationForm.LocationDescription.trim() || 'Location Name',
                      )
                    : [
                        { no: -1, name: `${locationForm.LocationDescription.trim() || 'Location Name'} Temperature` },
                        { no: -2, name: `${locationForm.LocationDescription.trim() || 'Location Name'} Humidity` },
                      ]
                  ).map(({ no, name }) => (
                    <Typography
                      key={no}
                      variant="body2"
                      sx={{
                        fontSize: '0.8rem',
                        color:
                          locationForm.LocationDescription.trim() && enviroExpandChannels.length > 0
                            ? 'text.primary'
                            : 'text.disabled',
                        lineHeight: 1.7,
                      }}
                    >
                      • {name}
                    </Typography>
                  ))}
                </Box>
              )}

              {/* Function: concrete hardware picks Temperature or Temperature +
                  Maturity (a ConXagent picks per channel, in its rows); site
                  hardware's follows from the device. */}
              {locationForm.HardwareType && (dialogFunctionView.kind === 'fixed' || !multiMode) && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
                  <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
                    Function
                  </Typography>
                  {dialogFunctionView.kind === 'fixed' ? (
                    <Typography variant="body2">{dialogFunctionView.label}</Typography>
                  ) : (
                    <LocationFunctionToggle
                      value={locationForm.CalculateMaturity ? 'maturity' : 'temp'}
                      maturityAllowed={computesMaturity}
                      maturityName={dialogMaturityName}
                      onChange={(fn) => {
                        setLocationForm((prev) => ({
                          ...prev,
                          CalculateMaturity: fn === 'maturity',
                          IsAmbient: false,
                          EAStart: fn === 'maturity' ? prev.EAStart || prev.LogginStart : '',
                        }));
                      }}
                    />
                  )}
                </Box>
              )}

              {/* Tank (only for TMC) */}
              {isLocFieldVisible('tank') && (
                <FormControl fullWidth required size="small">
                  <InputLabel>Tank</InputLabel>
                  <Select
                    value={locationForm.TankID}
                    label="Tank"
                    onChange={(e: SelectChangeEvent) => setLocationForm({ ...locationForm, TankID: e.target.value })}
                  >
                    {availableTanks(locationForm.TankID).map((t) => (
                      <MenuItem key={t.id} value={t.id}>{t.name}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              )}
            </Box>
          </DialogStep>

          <DialogStep n={4} title="Times">
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 1.5, alignItems: 'start' }}>
              <DateTimePicker
                label={tzLabelled('Start time', locationForm.LogginStart)}
                value={locationForm.LogginStart ? dayjs(locationForm.LogginStart) : null}
                onChange={(val: Dayjs | null) => {
                  const next = val ? val.format('YYYY-MM-DDTHH:mm') : '';
                  // Adding: the concrete start follows Start while it is empty or
                  // still equal to the old Start. Editing keeps it as set.
                  const follows = editingLocationIdx === null && isLocFieldVisible('concreteTemperatureStartTime');
                  setLocationForm((prev) => ({
                    ...prev,
                    LogginStart: next,
                    ...(follows && (prev.EAStart === '' || prev.EAStart === prev.LogginStart) ? { EAStart: next } : {}),
                  }));
                }}
                minutesStep={5}
                ampm={false}
                slotProps={{
                  textField: { fullWidth: true, size: 'small', required: true },
                  actionBar: { sx: { display: 'none' } },
                  popper: { sx: PICKER_POPPER_SX },
                }}
              />
              {dialogConcreteStartVisible && (
                // The full name and what it does are the field's tooltip.
                <Box title={`${concreteStartLabel}. ${concreteStartHelp}`}>
                  <DateTimePicker
                    label={tzLabelled(CONCRETE_START_SHORT_LABEL, locationForm.EAStart)}
                    value={locationForm.EAStart ? dayjs(locationForm.EAStart) : null}
                    onChange={(val: Dayjs | null) => setLocationForm({ ...locationForm, EAStart: val ? val.format('YYYY-MM-DDTHH:mm') : '' })}
                    minutesStep={5}
                    ampm={false}
                    slotProps={{
                      textField: {
                        fullWidth: true,
                        size: 'small',
                        helperText: 'Maturity counts from this time.',
                        sx: { '& .MuiFormHelperText-root': { ml: 0, mt: 0.25, fontSize: '0.7rem' } },
                      },
                      actionBar: { sx: { display: 'none' } },
                      popper: { sx: PICKER_POPPER_SX },
                    }}
                  />
                </Box>
              )}
              <DateTimePicker
                label={tzLabelled('Stop time', locationForm.LoggingEnd)}
                value={locationForm.LoggingEnd ? dayjs(locationForm.LoggingEnd) : null}
                onChange={(val: Dayjs | null) => setLocationForm({ ...locationForm, LoggingEnd: val ? val.format('YYYY-MM-DDTHH:mm') : '' })}
                minutesStep={5}
                ampm={false}
                slotProps={{
                  textField: { fullWidth: true, size: 'small' },
                  actionBar: { sx: { display: 'none' } },
                  popper: { sx: PICKER_POPPER_SX },
                }}
              />
              {isLocFieldVisible('evaporationStopTime') && (
                <DateTimePicker
                  label={tzLabelled('Evaporation stop', locationForm.EvaporationStopTime)}
                  value={locationForm.EvaporationStopTime ? dayjs(locationForm.EvaporationStopTime) : null}
                  onChange={(val: Dayjs | null) => setLocationForm({ ...locationForm, EvaporationStopTime: val ? val.format('YYYY-MM-DDTHH:mm') : '' })}
                  minutesStep={5}
                  ampm={false}
                  slotProps={{
                    textField: { fullWidth: true, size: 'small' },
                    actionBar: { sx: { display: 'none' } },
                    popper: { sx: PICKER_POPPER_SX },
                  }}
                />
              )}
            </Box>
          </DialogStep>

          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', borderTop: '1px solid', borderColor: 'grey.200', pt: 1.5 }}>
            Temperature limits, differentials and evaporation are switched on per location in the table after saving.
          </Typography>
        </Box>
        {/* Buttons wrap, and stack full width on a phone. */}
        <Box
          sx={{
            px: 3,
            py: 1.5,
            borderTop: '1px solid',
            borderColor: 'grey.200',
            display: 'flex',
            justifyContent: 'flex-end',
            flexWrap: 'wrap',
            gap: 1,
            ...(isMobile && { flexDirection: 'column-reverse', alignItems: 'stretch' }),
          }}
        >
          {editingLocationIdx !== null && (
            <Button
              onClick={() => {
                deleteLocation(editingLocationIdx);
                setLocationDialogOpen(false);
              }}
              variant="text"
              color="error"
              fullWidth={isMobile}
              startIcon={<DeleteIcon fontSize="small" />}
              sx={{ textTransform: 'none', fontWeight: 500, mr: isMobile ? 0 : 'auto' }}
            >
              Delete
            </Button>
          )}
          <Button
            onClick={() => setLocationDialogOpen(false)}
            variant="text"
            fullWidth={isMobile}
            sx={{ textTransform: 'none', color: 'text.secondary', fontWeight: 500 }}
          >
            {addedCount > 0 ? 'Close' : 'Cancel'}
          </Button>
          {/* Arrow functions: a bare onClick={saveLocation} would pass the
              click event as addNext. */}
          {editingLocationIdx === null && (
            <Button
              onClick={() => saveLocation(true)}
              variant="outlined"
              fullWidth={isMobile}
              sx={{ textTransform: 'none', fontWeight: 600 }}
            >
              {'Save & add next device'}
            </Button>
          )}
          <Button
            onClick={() => saveLocation(false)}
            variant="contained"
            fullWidth={isMobile}
            sx={{ textTransform: 'none', px: 4, fontWeight: 600 }}
          >
            Save
          </Button>
        </Box>
      </Dialog>
    </Box>
  );

  const renderStep3 = () => {
    const availableContacts = lookups?.contacts.filter(
      (c) => !contacts.some((added) => added.contactId === c.id),
    ) ?? [];

    return (
      <Box>
        <Typography variant="h6" sx={{ mb: 2 }}>
          Notifications / Alerts
        </Typography>

        {isMobile ? (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, mb: 2 }}>
            {contacts.length === 0 ? (
              <Paper variant="outlined" sx={{ p: 3, textAlign: 'center', borderStyle: 'dashed' }}>
                <Typography variant="body2" color="text.secondary">
                  No contacts added. Tap &ldquo;Add a contact&rdquo; below to send notifications.
                </Typography>
              </Paper>
            ) : (
              contacts.map((c, idx) => (
                <Card key={c.contactId} elevation={1}>
                  <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 1 }}>
                      <Box sx={{ minWidth: 0 }}>
                        <Typography variant="subtitle2" fontWeight={600} noWrap>
                          {contactName(c.contactId)}
                        </Typography>
                        <Box sx={{ display: 'flex', gap: 0.75, mt: 1, flexWrap: 'wrap' }}>
                          <Chip
                            label="Email"
                            size="small"
                            variant={c.sendEmail ? 'filled' : 'outlined'}
                            color={c.sendEmail ? 'primary' : 'default'}
                            onClick={() => toggleContactField(idx, 'sendEmail')}
                          />
                          <Chip
                            label="Alerts"
                            size="small"
                            variant={c.alert ? 'filled' : 'outlined'}
                            color={c.alert ? 'primary' : 'default'}
                            onClick={() => toggleContactField(idx, 'alert')}
                          />
                        </Box>
                      </Box>
                      <IconButton size="small" color="error" onClick={() => removeContact(idx)} sx={{ flexShrink: 0 }}>
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Box>
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                      Tap a chip to toggle.
                    </Typography>
                  </CardContent>
                </Card>
              ))
            )}
          </Box>
        ) : (
        <TableContainer component={Paper} sx={{ mb: 2 }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Contact</TableCell>
                <TableCell align="center">Email</TableCell>
                <TableCell align="center">Alert</TableCell>
                <TableCell width={50} />
              </TableRow>
            </TableHead>
            <TableBody>
              {contacts.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={4} align="center" sx={{ py: 4 }}>
                    <Typography variant="body2" color="text.secondary">
                      No contacts added. Click the button below to add notification contacts.
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                contacts.map((c, idx) => (
                  <TableRow key={c.contactId}>
                    <TableCell>{contactName(c.contactId)}</TableCell>
                    <TableCell align="center">
                      <Checkbox checked={c.sendEmail} onChange={() => toggleContactField(idx, 'sendEmail')} size="small" />
                    </TableCell>
                    <TableCell align="center">
                      <Checkbox checked={c.alert} onChange={() => toggleContactField(idx, 'alert')} size="small" />
                    </TableCell>
                    <TableCell>
                      <IconButton size="small" color="error" onClick={() => removeContact(idx)}>
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
        )}

        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => {
            setNewContactId('');
            setContactDialogOpen(true);
          }}
          disabled={availableContacts.length === 0}
          fullWidth={isMobile}
          sx={{ textTransform: 'none', ...(isMobile && { py: 1.25 }) }}
        >
          ADD A CONTACT
        </Button>

        {/* Contact Selector Dialog */}
        <Dialog open={contactDialogOpen} onClose={() => setContactDialogOpen(false)} maxWidth="xs" fullWidth fullScreen={isMobile}>
          <DialogTitle>Add Contact</DialogTitle>
          <DialogContent dividers>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
              <FormControl size="small" required>
                <InputLabel>Contact</InputLabel>
                <Select
                  value={newContactId}
                  label="Contact"
                  onChange={(e: SelectChangeEvent) => setNewContactId(e.target.value)}
                >
                  <MenuItem value="">
                    <em>None</em>
                  </MenuItem>
                  {availableContacts.map((c) => (
                    <MenuItem key={c.id} value={c.id}>
                      {c.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setContactDialogOpen(false)}>Cancel</Button>
            <Button variant="contained" onClick={addContact} disabled={!newContactId}>
              Add
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    );
  };

  // ─── Step 5: Review ───
  const renderStep4 = () => {
    const projectName = lookups?.projects.find((p) => p.id === projectId)?.name || '-';
    const stopDescription = describeStop(stopType, xHours, '');

    // A titled block with an Edit link back to its step.
    const section = (title: string, step: number, children: React.ReactNode, testId?: string) => (
      <Paper sx={{ p: 2, mb: 2 }} data-testid={testId}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5, gap: 1 }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>{title}</Typography>
          <Button
            size="small"
            startIcon={<EditIcon fontSize="small" />}
            onClick={() => goToStep(step)}
            aria-label={`${t('pourCreate.review.edit', 'Edit')} ${title}`}
            sx={{ textTransform: 'none', minHeight: 0 }}
          >
            {t('pourCreate.review.edit', 'Edit')}
          </Button>
        </Box>
        {children}
      </Paper>
    );

    const row = (label: string, value: React.ReactNode) => (
      <Box key={label} sx={{ display: 'grid', gridTemplateColumns: { xs: '130px 1fr', sm: '200px 1fr' }, gap: 0.75, mb: 0.5 }}>
        <Typography variant="body2" color="text.secondary">{label}</Typography>
        <Box sx={{ typography: 'body2', minWidth: 0 }}>{value || '-'}</Box>
      </Box>
    );

    // What the pour computes maturity with once saved: the mix design's
    // maturity input group, else the defaults the check reports.
    const hasGroup = !!productDetail?.maturity_method;
    const method = productDetail?.maturity_method || plannedBasis?.method || DEFAULT_MATURITY_METHOD;
    const nurseSaul = method === 'Nurse Saul';
    const datumTemp = hasGroup ? productDetail?.maturity_datum_temp : plannedBasis?.datum_temp;
    const meta = describeMaturity(
      method,
      hasGroup ? productDetail?.maturity_specified_temp : plannedBasis?.specified_temp,
    );
    const inputsText = nurseSaul
      ? datumTemp != null
        ? `${t('pourCreate.review.datum', 'Datum temperature')} ${datumTemp} °C`
        : t('pourCreate.review.datumDefault', 'Datum temperature: the default')
      : [
          qDisplay != null
            ? `Q ${qDisplay} K${plannedBasis?.q_is_default ? ` (${t('pourCreate.review.default', 'default')})` : ''}`
            : '',
          activationEnergyDisplay != null ? `${activationEnergyDisplay} J/mol` : '',
          `Ts ${meta.specified_temp ?? 20} °C`,
        ].filter(Boolean).join(', ');

    // The calibration's tested range, when its curve has samples. A
    // calibration with no recorded basis has no unit of its own; the pour
    // reads its curve in the pour's unit, as the targets below do.
    const summary = selectedCalibration?.summary ?? null;
    const range = summary?.calibrated_range ?? null;
    const rangeUnit = summary?.unit || meta.unit;
    const rangeText = range && range.has_curve && range.has_samples
      ? [
          range.s_min != null && range.s_max != null
            ? `${t('pourCreate.review.strength', 'Strength')} ${range.s_min.toFixed(1)} to ${valueWithUnit(range.s_max.toFixed(1), productStrengthUnit)}`
            : '',
          range.min_m != null && range.max_m != null
            ? `${t('pourCreate.review.maturity', 'Maturity')} ${Math.round(range.min_m)} to ${Math.round(range.max_m)} ${rangeUnit}`
            : '',
        ].filter(Boolean).join(', ')
      : '';

    // The group's targets (strength in the company unit, maturity with its
    // unit and Ts), flagged outside the tested range.
    const targets = Array.isArray(reviewTargets) ? reviewTargets : [];
    const strengthOf = (target: DefaultTarget): number | null => {
      const s = target.strength != null ? Number(target.strength) : NaN;
      return Number.isFinite(s) && s > 0 ? s : null;
    };
    const isOutsideRange = (target: DefaultTarget): boolean => {
      const s = strengthOf(target);
      return s != null && targetRangeFlag(s, range) != null;
    };
    const outsideRange = targets.filter(isOutsideRange);

    // Things to look at before saving.
    const warnings: string[] = [];
    if (computesMaturity && selectedProduct && productDetail && !hasGroup) {
      warnings.push(t(
        'pourCreate.review.noMaturityGroup',
        'The mix design has no maturity input group, so the pour uses the default maturity inputs.',
      ));
    }
    if (computesMaturity && noCalibration) warnings.push(noCalibration);
    if (computesMaturity && checkStatus === 'mismatch') {
      warnings.push(t(
        'pourCreate.review.basisMismatch',
        'The pour and its calibration use different maturity functions (ASTM C1074 9.2). Saving is disabled.',
      ));
    } else if (computesMaturity && checkStatus === 'differs') {
      warnings.push(
        calibrationCheck.acknowledged
          ? t(
              'pourCreate.review.basisDiffersConfirmed',
              'The pour and its calibration use different maturity inputs (ASTM C1074 9.2). You confirmed the difference.',
            )
          : t(
              'pourCreate.review.basisDiffers',
              'The pour and its calibration use different maturity inputs (ASTM C1074 9.2). Confirm the difference to save.',
            ),
      );
    }
    if (outsideRange.length > 0) {
      warnings.push(
        `${t('pourCreate.review.targetsOutsideRange', "Targets outside the calibration's tested range:")} ${outsideRange
          .map((target) => target.purpose || targetStrengthText(strengthOf(target), productStrengthUnit))
          .join(', ')}`,
      );
    }
    if (locations.length === 0) {
      warnings.push(t(
        'pourCreate.review.noLocations',
        'No monitoring locations: the pour records nothing until a location is added on Hardware / Sensors.',
      ));
    }
    warnings.push(...stationNotes);

    const showTargets = isFieldVisible('maturityTargetGroup') && !!targetGroupId;
    const targetGroupName =
      productTargetGroups.find((tg) => tg.target_group_id === targetGroupId)?.target_group_name || targetGroupId;

    const renderTargets = () => {
      if (!showTargets) {
        return (
          <Typography variant="body2" color="text.secondary">
            {t('pourCreate.review.noTargets', 'No maturity target group: the pour has no targets.')}
          </Typography>
        );
      }
      if (reviewTargets === null) {
        return (
          <Typography variant="body2" color="text.secondary">
            {t('pourCreate.review.loadingTargets', 'Loading the targets...')}
          </Typography>
        );
      }
      if (reviewTargets === 'failed') {
        return (
          <Typography variant="body2" color="text.secondary">
            {t('pourCreate.review.targetsFailed', 'The targets could not be loaded. They are copied from the group on save.')}
          </Typography>
        );
      }
      if (targets.length === 0) {
        return (
          <Typography variant="body2" color="text.secondary">
            {t('pourCreate.review.emptyGroup', 'The target group has no targets.')}
          </Typography>
        );
      }
      return (
        <TableContainer>
          <Table size="small" data-testid="review-targets">
            <TableHead>
              <TableRow>
                <TableCell>{t('pourCreate.review.purpose', 'Purpose')}</TableCell>
                <TableCell>{labelWithUnit(t('pourCreate.review.strength', 'Strength'), productStrengthUnit)}</TableCell>
                <TableCell>{`${t('pourCreate.review.maturity', 'Maturity')}, ${maturityShortTitle(meta)}`}</TableCell>
                <TableCell />
              </TableRow>
            </TableHead>
            <TableBody>
              {targets.map((target) => {
                const strength = strengthOf(target);
                const maturity = target.target != null ? Number(target.target) : NaN;
                return (
                  <TableRow key={target.target_id}>
                    <TableCell>{target.purpose || '-'}</TableCell>
                    <TableCell>{targetStrengthText(strength, productStrengthUnit) || '-'}</TableCell>
                    <TableCell>
                      {Number.isFinite(maturity) && maturity > 0
                        ? formatMaturity(maturity, meta)
                        : pourCalibrationId
                          ? t('pourCreate.review.fromCurve', 'From the calibration curve')
                          : '-'}
                    </TableCell>
                    <TableCell>
                      {isOutsideRange(target) && (
                        <Chip
                          size="small"
                          color="warning"
                          variant="outlined"
                          label={t('pourCreate.review.outsideRange', 'Outside tested range')}
                        />
                      )}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      );
    };

    return (
      <Box>
        <Typography variant="h6" sx={{ mb: 1 }}>{t('pourCreate.review.title', 'Review & Confirm')}</Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          {t(
            'pourCreate.review.intro',
            'Check the setup below. Click SAVE to create the pour, or use Edit or BACK to change a step.',
          )}
        </Typography>

        {warnings.length > 0 && (
          <Alert severity="warning" sx={{ mb: 2 }} data-testid="review-warnings">
            <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
              {t('pourCreate.review.warningsTitle', 'Check before saving')}
            </Typography>
            <Box component="ul" sx={{ m: 0, pl: 2.5 }}>
              {warnings.map((w) => (
                <Typography component="li" variant="body2" key={w}>{w}</Typography>
              ))}
            </Box>
          </Alert>
        )}

        {section(t('pourCreate.review.functions', 'Monitoring Functions'), 0, (
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
            {selectedFunctions.length === 0
              ? <Typography variant="body2" color="text.secondary">None selected</Typography>
              : selectedFunctions.map((f) => (
                  <Chip
                    key={f}
                    label={`${f}${isAutoIncluded(f) ? ' (required)' : ''}`}
                    size="small"
                    color="primary"
                    variant={isAutoIncluded(f) ? 'outlined' : 'filled'}
                  />
                ))}
          </Box>
        ))}

        {section(t('pourCreate.review.details', 'Pour Details'), 1, (
          <>
            {row('Pour Name', pourName)}
            {row('Project', projectName)}
            {row(
              t('pourCreate.review.timezone', 'Timezone'),
              projectTzIana
                ? `${projectTzIana} (${zoneOf(pourStartTime)})`
                : t('pourCreate.review.noTimezone', 'UTC (the project has no timezone)'),
            )}
            {isFieldVisible('productCode') && row('Mix Design', selectedProduct ? (
              <>
                {formatProductOption(selectedProduct)}
                {selectedProduct.strength_grade != null && (
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                    {valueWithUnit(selectedProduct.strength_grade, productStrengthUnit)}
                  </Typography>
                )}
              </>
            ) : '-')}
            {isFieldVisible('maturityTargetGroup') &&
              row('Maturity Target Group', targetGroupId ? targetGroupName : t('pourCreate.review.none', 'None'))}
            {isFieldVisible('massConcreteLimitGroup') && limitGroupId &&
              row('Mass Concrete Limits', lookups?.mass_concrete_defaults.find((m) => m.id === limitGroupId)?.name || limitGroupId)}
            {isFieldVisible('evaporationLimitGroup') && evaLimitGroupId &&
              row('Evaporation Limits', lookups?.evaporation_defaults.find((e) => e.id === evaLimitGroupId)?.name || evaLimitGroupId)}
            {row('Pour Start Time', reviewTime(pourStartTime))}
            {row('Pour Finish Time', reviewTime(pourFinishTime))}
            {row(
              t('pourCreate.review.sensorsStart', 'Sensors start'),
              startAllSensors
                ? t('pourCreate.review.sensorsStartAtPour', 'At the pour start time')
                : t('pourCreate.review.sensorsStartOwn', "At each location's own start time"),
            )}
            {row(t('pourCreate.review.stop', 'Stop monitoring'), stopDescription)}
            {pourVolume && row('Pour Volume', `${pourVolume} m³`)}
            {location && row('Pour Location', location)}
            {projectRef && row('Drawing / Reference', projectRef)}
            {stagedFiles.length > 0 && row('Attachments', `${stagedFiles.length} file${stagedFiles.length === 1 ? '' : 's'}`)}
          </>
        ))}

        {computesMaturity && selectedProduct && section(t('pourCreate.review.maturityTitle', 'Maturity and Strength'), 1, (
          <>
            {row(
              t('pourCreate.review.method', 'Maturity method'),
              // "Nurse-Saul TTF (°C·hrs)"; equivalent age adds its Ts:
              // "Arrhenius equivalent age, EA at 20 °C (hrs)".
              nurseSaul
                ? `${describeMethod(method)} (${meta.unit})`
                : `${describeMethod(method)}, ${maturityShortTitle(meta)}`,
            )}
            {row(t('pourCreate.review.inputs', 'Maturity inputs'), inputsText)}
            {row(
              t('pourCreate.review.inputsFrom', 'Inputs from'),
              hasGroup
                ? `${t('pourCreate.review.group', 'Maturity input group')} ${productDetail?.maturity_group_description || productDetail?.maturity_group_id || ''}`.trim()
                : t('pourCreate.review.defaults', 'The default inputs (no maturity input group)'),
            )}
            {row(t('pourCreate.review.calibration', 'Calibration'), calibrationLabel || t('pourCreate.review.none', 'None'))}
            {rangeText && row(t('pourCreate.review.testedRange', 'Tested range'), rangeText)}
            <Box sx={{ mt: 1 }}>
              {laterStepCheck ? (
                <Typography variant="body2" color="text.secondary">
                  {t('pourCreate.review.checkAbove', 'The calibration check is shown above.')}
                </Typography>
              ) : (
                calibrationCheckPanel
              )}
            </Box>
            <Typography variant="body2" sx={{ fontWeight: 600, mt: 2, mb: 0.5 }}>
              {t('pourCreate.review.targets', 'Targets')}
              {showTargets ? ` (${targetGroupName})` : ''}
            </Typography>
            {renderTargets()}
            {mixDesignActions}
          </>
        ), 'review-maturity')}

        {section(
          `Hardware / Sensors (${locations.length} location${locations.length === 1 ? '' : 's'})`,
          2,
          locations.length === 0 ? (
            <Typography variant="body2" color="warning.main">
              No monitoring locations configured. Go back to Hardware / Sensors to add at least one.
            </Typography>
          ) : (
            locations.map((loc, idx) => {
              const logger = lookups?.loggers.find((l) => l.id === loc.LoggerID);
              const site = isSiteHardware(loc.HardwareType);
              const channelShown = HARDWARE_TYPE_FIELDS[loc.HardwareType]?.channelNumber !== false;
              const flags: string[] = [];
              if (loc.CalculateMaturity && !site) flags.push('Maturity');
              if (loc.IsAmbient && !site) flags.push('Ambient');
              if (loc.CalculateTMC) flags.push('TMC');
              if (loc.CalculateEvaporation) flags.push('Evaporation');
              if (loc.ApplyTemperatureLimits && isFieldVisible('temperatureLimits')) flags.push('Temp Limits');
              if (showDiffColumn && symmetricPartners(locations, idx).length > 0) flags.push('Differential');
              return (
                <Box
                  key={idx}
                  data-testid="review-location"
                  sx={{ py: 0.75, borderBottom: idx < locations.length - 1 ? '1px solid' : 'none', borderColor: 'divider' }}
                >
                  <Typography variant="body2" fontWeight={500}>
                    {loc.LocationDescription || `Location ${idx + 1}`}
                  </Typography>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                    {[
                      loc.HardwareType,
                      `${loggerDisplayName(logger) || loc.LoggerID || '-'}${channelShown ? ` ch.${loc.ChannelNo}` : ''}`,
                      ...flags,
                    ].filter(Boolean).join(' · ')}
                  </Typography>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                    {`Start ${reviewTime(loc.LogginStart)}`}
                    {loc.CalculateMaturity && !site ? ` · ${concreteStartLabel} ${reviewTime(loc.EAStart)}` : ''}
                  </Typography>
                </Box>
              );
            })
          ),
        )}

        {section(`Notifications / Alerts (${contacts.length} contact${contacts.length === 1 ? '' : 's'})`, 3, (
          contacts.length === 0 ? (
            <Typography variant="body2" color="text.secondary">
              No contacts added. Notifications will not be sent.
            </Typography>
          ) : (
            contacts.map((c, idx) => (
              <Box key={idx} sx={{ display: 'flex', justifyContent: 'space-between', py: 0.5 }}>
                <Typography variant="body2">{contactName(c.contactId)}</Typography>
                <Box sx={{ display: 'flex', gap: 1 }}>
                  {c.sendEmail && <Chip label="Email" size="small" variant="outlined" />}
                  {c.alert && <Chip label="Alerts" size="small" color="primary" variant="outlined" />}
                </Box>
              </Box>
            ))
          )
        ))}
      </Box>
    );
  };

  const stepContent = [renderStep0, renderStep1, renderStep2, renderStep3, renderStep4];

  return (
    <Box
      sx={{
        maxWidth: 1600,
        mx: 'auto',
        px: { xs: 0, md: 5 },
        pb: { xs: 12, md: 6 },
        display: 'flex',
        flexDirection: 'column',
        // Fill the viewport below the app chrome so the action bar can pin to
        // the bottom of the page even on short steps (mt: 'auto' below).
        minHeight: { md: 'calc(100vh - 170px)' },
      }}
    >
      {/* Stepper — compact progress bar on mobile, full icon stepper on desktop */}
      {isMobile ? (
        <Box sx={{ mb: 3 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', mb: 1 }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
              {STEPS[activeStep]}
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ flexShrink: 0, ml: 1 }}>
              Step {activeStep + 1} of {STEPS.length}
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', gap: 0.75 }}>
            {STEPS.map((label, idx) => {
              const reached = idx <= activeStep;
              const visited = idx <= furthestStep;
              return (
                <Box
                  key={label}
                  onClick={() => goToStep(idx)}
                  role="button"
                  aria-label={`Go to ${label}`}
                  sx={mobileProgressSegmentSx(reached, visited)}
                />
              );
            })}
          </Box>
        </Box>
      ) : (
      <Stepper
        activeStep={activeStep}
        alternativeLabel
        // STEPPER_SX is sized for 4 steps; with 5 the side margin that puts
        // the end icons flush with the content is W / 8 - 25px.
        sx={{ ...STEPPER_SX, mx: 'calc(-100% / 8 + 25px)' }}
      >
        {/* A step already reached can be opened again by its label. */}
        {STEPS.map((label, idx) => (
          <Step
            key={label}
            completed={idx < furthestStep && isStepValid(idx)}
            onClick={() => goToStep(idx)}
            sx={{ cursor: idx <= furthestStep && idx !== activeStep ? 'pointer' : 'default' }}
          >
            <StepLabel>{label}</StepLabel>
          </Step>
        ))}
      </Stepper>
      )}

      {/* Alerts */}
      {duplicateSourceLabel && (
        <Alert severity="info" sx={{ mb: 2 }}>
          {t('pourCreate.duplicateBanner', {
            defaultValue:
              'Duplicating "{{code}}": details are prefilled. Review the pour date, name and logger start times before saving.',
            code: duplicateSourceLabel,
          })}
        </Alert>
      )}
      {error && (
        <Alert severity="error" sx={{ mb: 2, whiteSpace: 'pre-line' }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}
      {success && (
        <Alert severity="success" sx={{ mb: 2 }}>
          {success}
        </Alert>
      )}
      {/* After Pour Details, a difference to confirm or a mismatch shows on
          every step, so it can be settled where SAVE / NEXT is held. */}
      {laterStepCheck && (
        <Box sx={{ mb: 2 }} data-testid="calibration-check-later-step">
          {calibrationCheckPanel}
        </Box>
      )}

      {/* Step Content */}
      <Box sx={{ mb: 4, minHeight: 400 }}>{stepContent[activeStep]()}</Box>

      {/* Bottom Navigation — sticky thumb-reachable bar on mobile */}
      {isMobile ? (
        <Box
          sx={{
            position: 'sticky',
            bottom: 0,
            zIndex: 10,
            mt: 3,
            py: 1.5,
            backgroundColor: 'background.paper',
            borderTop: '1px solid',
            borderColor: 'divider',
            display: 'flex',
            flexDirection: 'column',
            gap: 1,
          }}
        >
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
            {activeStep > 0 && (
              <Button
                variant="outlined"
                onClick={handleBack}
                disabled={saving}
                startIcon={<ArrowBackIcon />}
                sx={{ textTransform: 'none', py: 1.25, borderColor: 'grey.400', color: 'text.primary', flexShrink: 0 }}
              >
                Back
              </Button>
            )}
            <Tooltip describeChild title={(primaryBlocked && basisBlock) || ''}>
              <span style={{ flex: 1, display: 'flex' }}>
                <Button
                  variant="contained"
                  onClick={handleNext}
                  disabled={saving || primaryBlocked}
                  fullWidth
                  startIcon={saving ? <CircularProgress size={20} color="inherit" /> : activeStep === STEPS.length - 1 ? <SaveIcon /> : undefined}
                  sx={{ textTransform: 'none', py: 1.25, flex: 1, ...PRIMARY_ACTION_SX }}
                >
                  {saving ? 'Saving...' : activeStep === STEPS.length - 1 ? 'Save pour' : 'Save / Next'}
                </Button>
              </span>
            </Tooltip>
          </Box>
          <Button
            variant="text"
            onClick={handleCancel}
            disabled={saving}
            sx={{ textTransform: 'none', color: 'text.secondary', fontWeight: 500, alignSelf: 'center' }}
          >
            Cancel / Return
          </Button>
        </Box>
      ) : (
      <Box
        sx={{
          // Pinned to the bottom of the viewport so SAVE is always reachable,
          // even on long steps; mt: 'auto' keeps it at the page foot on short ones.
          position: 'sticky',
          bottom: 0,
          zIndex: 10,
          mt: 'auto',
          py: 3,
          backgroundColor: 'common.white',
          borderTop: '1px solid',
          borderColor: 'divider',
          display: 'flex',
          gap: 3,
          justifyContent: 'center',
          alignItems: 'center',
        }}
      >
        {activeStep > 0 && (
          <Button
            variant="outlined"
            onClick={handleBack}
            disabled={saving}
            sx={{ textTransform: 'none', px: 5, py: 1.25, borderColor: 'grey.400', color: 'text.primary' }}
          >
            BACK
          </Button>
        )}
        <Tooltip describeChild title={(primaryBlocked && basisBlock) || ''}>
          <span style={{ display: 'inline-flex' }}>
            <Button
              variant="contained"
              onClick={handleNext}
              disabled={saving || primaryBlocked}
              startIcon={saving ? <CircularProgress size={20} color="inherit" /> : activeStep === STEPS.length - 1 ? <SaveIcon /> : undefined}
              sx={{ textTransform: 'none', px: 6, py: 1.25, minWidth: 200, ...PRIMARY_ACTION_SX }}
            >
              {saving ? 'Saving...' : activeStep === STEPS.length - 1 ? 'SAVE' : 'SAVE / NEXT'}
            </Button>
          </span>
        </Tooltip>
        <Button
          variant="text"
          onClick={handleCancel}
          disabled={saving}
          sx={{ textTransform: 'none', color: 'text.secondary', fontWeight: 500 }}
        >
          Cancel / Return
        </Button>
      </Box>
      )}

      {/* Cancel / Return, a sidebar link or Back / Forward with unsaved
          entries. While saving (only a link or Back / Forward can ask then)
          the create is already sent, so leaving does not discard the pour. */}
      <Dialog open={leaveDialogOpen} onClose={keepEditing} maxWidth="xs" fullWidth>
        <DialogTitle>{saving ? 'Leave before the save finishes?' : 'Leave without saving?'}</DialogTitle>
        <DialogContent>
          <Typography>
            {saving
              ? 'This pour is still being saved. It will be created even if you leave now.'
              : 'This pour has not been created yet. Leaving now discards what you entered.'}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={keepEditing} autoFocus sx={{ textTransform: 'none' }}>
            Keep editing
          </Button>
          <Button
            onClick={leaveWithoutSaving}
            color="error"
            variant="contained"
            sx={{ textTransform: 'none' }}
          >
            {saving ? 'Leave now' : 'Leave without saving'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Image attachment lightbox */}
      <ImagePreviewDialog
        src={imagePreview?.src ?? null}
        name={imagePreview?.name}
        onClose={() => setImagePreview(null)}
      />

      {/* ── Add Project Dialog ── */}
      <Dialog open={addProjectOpen} onClose={resetProjectDialog} maxWidth="sm" fullWidth fullScreen={isMobile}>
        <DialogTitle>Add New Project</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5, mt: 1 }}>
            <TextField
              label="Project Name"
              value={newProjectForm.name}
              onChange={(e) => setNewProjectForm((p) => ({ ...p, name: e.target.value }))}
              fullWidth
              required
              autoFocus
            />
            <Autocomplete
              freeSolo
              fullWidth
              options={clientOptions ?? []}
              value={newProjectForm.client || null}
              inputValue={newProjectForm.client}
              onInputChange={(_, value) => setNewProjectForm((p) => ({ ...p, client: value }))}
              renderInput={(params) => (
                <TextField {...params} label="Client" placeholder="Select or type a client" />
              )}
            />
            <AzureAddressAutocomplete
              label="Address"
              value={newProjectForm.address}
              placeholder="e.g. 123 Main Street, Sydney NSW 2000"
              onChange={(address, lat, lon) => {
                setNewProjectForm((p) => ({ ...p, address }));
                if (lat && lon) setNewProjectLatLon({ lat, lon });
              }}
            />
            <Autocomplete
              fullWidth
              options={TIMEZONES}
              getOptionLabel={(option: TimezoneOption) => option.label}
              isOptionEqualToValue={(option, val) => option.value === val.value}
              value={TIMEZONES.find((tz) => tz.value === newProjectForm.timezone) || null}
              onChange={(_, selected) => setNewProjectForm((p) => ({ ...p, timezone: selected?.value || '' }))}
              renderInput={(params) => <TextField {...params} label="Timezone" />}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={resetProjectDialog} sx={{ textTransform: 'none' }}>
            Cancel
          </Button>
          <Button
            onClick={handleAddProject}
            variant="contained"
            disabled={!newProjectForm.name.trim() || addProjectSaving}
            sx={{ textTransform: 'none' }}
          >
            {addProjectSaving ? 'Adding...' : 'Add Project'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── Add Product Dialog ── */}
      <Dialog open={addProductOpen} onClose={resetProductDialog} maxWidth="sm" fullWidth fullScreen={isMobile}>
        <DialogTitle>Add New Product / Recipe</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5, mt: 1 }}>
            <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 2 }}>
              <TextField
                label="Product Code"
                value={newProductForm.code}
                onChange={(e) => setNewProductForm((p) => ({ ...p, code: e.target.value }))}
                fullWidth
                required
                autoFocus
                inputProps={{ maxLength: 50 }}
              />
              <TextField
                label={labelWithUnit('Strength Grade', ownStrengthUnit)}
                value={newProductForm.strength_grade}
                onChange={(e) => setNewProductForm((p) => ({ ...p, strength_grade: e.target.value }))}
                type="number"
                fullWidth
              />
            </Box>
            <TextField
              label="Description"
              value={newProductForm.description}
              onChange={(e) => setNewProductForm((p) => ({ ...p, description: e.target.value }))}
              fullWidth
              inputProps={{ maxLength: 50 }}
            />
            <FormControl fullWidth>
              <InputLabel>Supplier / Batch Plant</InputLabel>
              <Select
                value={newProductForm.supplier_plant_id}
                onChange={(e: SelectChangeEvent) => setNewProductForm((p) => ({ ...p, supplier_plant_id: e.target.value }))}
                label="Supplier / Batch Plant"
              >
                <MenuItem value=""><em>None</em></MenuItem>
                {supplierPlants.map((plant) => (
                  <MenuItem key={plant.supplier_plant_id} value={plant.supplier_plant_id}>
                    {plant.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <Typography variant="subtitle2" sx={{ fontWeight: 600, mt: 1, mb: -1 }}>Defaults</Typography>
            <FormControl fullWidth>
              <InputLabel>Mass Concrete Default</InputLabel>
              <Select
                value={newProductForm.mass_concrete_default_id}
                onChange={(e: SelectChangeEvent) => setNewProductForm((p) => ({ ...p, mass_concrete_default_id: e.target.value }))}
                label="Mass Concrete Default"
              >
                <MenuItem value=""><em>None</em></MenuItem>
                {(lookups?.mass_concrete_defaults || []).map((d) => (
                  <MenuItem key={d.id} value={d.id}>{d.name}</MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl fullWidth>
              <InputLabel>Evaporation Default</InputLabel>
              <Select
                value={newProductForm.evaporation_default_id}
                onChange={(e: SelectChangeEvent) => setNewProductForm((p) => ({ ...p, evaporation_default_id: e.target.value }))}
                label="Evaporation Default"
              >
                <MenuItem value=""><em>None</em></MenuItem>
                {(lookups?.evaporation_defaults || []).map((d) => (
                  <MenuItem key={d.id} value={d.id}>{d.name}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={resetProductDialog} sx={{ textTransform: 'none' }}>
            Cancel
          </Button>
          <Button
            onClick={handleAddProduct}
            variant="contained"
            disabled={!newProductForm.code.trim() || addProductSaving}
            sx={{ textTransform: 'none' }}
          >
            {addProductSaving ? 'Adding...' : 'Add Product'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Quick-add Batch Plant dialog */}
      <Dialog open={addPlantOpen} onClose={resetPlantDialog} maxWidth="sm" fullWidth>
        <DialogTitle>Add New Batch Plant</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5, mt: 1 }}>
            <TextField
              label="Batching Plant Name"
              value={newPlantForm.name}
              onChange={(e) => setNewPlantForm((p) => ({ ...p, name: e.target.value }))}
              fullWidth
              required
              autoFocus
              inputProps={{ maxLength: 200 }}
            />
            <TextField
              label="Ready Mix Supplier"
              value={newPlantForm.ready_mix_supplier}
              onChange={(e) => setNewPlantForm((p) => ({ ...p, ready_mix_supplier: e.target.value }))}
              fullWidth
              inputProps={{ maxLength: 200 }}
            />
            <AzureAddressAutocomplete
              label="Address"
              value={newPlantForm.address}
              placeholder="e.g. 4/3 Ascot Road, Ballina NSW 2478"
              onChange={(address, lat, lon) => {
                setNewPlantForm((p) => ({
                  ...p,
                  address,
                  ...(lat && lon ? { latitude: lat, longitude: lon } : {}),
                }));
              }}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={resetPlantDialog} sx={{ textTransform: 'none' }}>
            Cancel
          </Button>
          <Button
            onClick={handleAddPlant}
            variant="contained"
            disabled={!newPlantForm.name.trim() || addPlantSaving}
            sx={{ textTransform: 'none' }}
          >
            {addPlantSaving ? 'Adding...' : 'Add Batch Plant'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default PourCreatePage;
